import { escH, uploadWithProgress, createProgressBar, showAlert, showConfirm } from '../lib/helpers.js';
import QRCode from 'qrcode';

(() => {
  const AUTH_API = '';  // Same-origin — all /auth/* routes are on this worker
  const loginView   = document.getElementById('login-view');
  const accountView = document.getElementById('account-view');
  const loadingView = document.getElementById('loading-view');
  let currentUser = null;

  // Cached data
  let cachedFavorites = null;
  let cachedReviews = null;
  let cachedClaims = null;

  const getReturnPath = () => {
    const r = new URLSearchParams(location.search).get('return') || '/account/';
    if (!r.startsWith('/') || r.startsWith('//')) return '/account/';
    return r;
  };

  // Dev auth: pick up token from URL hash
  const devHash = new URLSearchParams(location.hash.replace(/^#/, ''));
  const hashToken = devHash.get('token');
  const devBusinessTarget = devHash.get('business');
  if (hashToken) {
    localStorage.setItem('ps_dev_token', hashToken);
    const cleanUrl = devBusinessTarget ? `${location.pathname}#/business/${encodeURIComponent(devBusinessTarget)}` : location.pathname + location.search;
    history.replaceState(null, '', cleanUrl);
  }
  let devToken = localStorage.getItem('ps_dev_token');

  // Development-only role switcher. The server decides whether it is enabled.
  fetch('/auth/dev-login', { credentials: 'include' })
    .then(r => r.json())
    .then(d => {
      if (!d.enabled) return;
      const panel = document.getElementById('dev-login-panel');
      if (panel) panel.classList.remove('hidden');
    })
    .catch(() => {});

  const authFetch = (url, opts) => {
    opts = opts || {};
    if (devToken) {
      opts.headers = opts.headers || {};
      opts.headers['Authorization'] = `Bearer ${devToken}`;
    }
    opts.credentials = 'include';
    return fetch(url, opts);
  };

  const mobileTabBar = document.getElementById('mobile-tab-bar');
  const show = (view) => {
    [loginView, accountView, loadingView].forEach((v) => {
      v.classList.add('hidden');
      v.style.display = 'none';
    });
    view.classList.remove('hidden');
    view.style.display = (view === loadingView || view === loginView) ? 'flex' : 'block';
    // Show/hide mobile tab bar
    if (mobileTabBar) mobileTabBar.style.display = (view === accountView) ? '' : 'none';
  };

  let managePlaceId = null; // currently managing this business
  let manageBizName = null;
  let ownerProfileUrl = '';
  let applyOwnerRoute = null;

  // ── Router ──
  const getPage = () => {
    const h = location.hash.replace(/^#\/?/, '') || '';
    if (h === 'favorites' || h === 'reviews' || h === 'sponsors' || h === 'settings') return h;
    if (h.indexOf('business/') === 0) return 'business';
    return 'dashboard';
  };

  const getBusinessPlaceId = () => {
    const h = location.hash.replace(/^#\/?/, '') || '';
    if (h.indexOf('business/') === 0) return decodeURIComponent(h.slice(9).split('/')[0]);
    return null;
  };

  const getBusinessRoute = () => {
    const parts = location.hash.replace(/^#\/?/, '').split('/');
    if (parts[0] !== 'business' || !parts[1]) return null;
    return { placeId: decodeURIComponent(parts[1]), kind: parts[2] || 'overview', value: parts[3] ? decodeURIComponent(parts[3]) : null };
  };

  const navigate = () => {
    const page = getPage();
    document.querySelectorAll('.page-content').forEach((el) => { el.classList.add('hidden'); });
    const target = document.getElementById(`page-${page}`);
    if (target) target.classList.remove('hidden');

    document.querySelectorAll('.nav-link').forEach((link) => {
      const linkPage = link.dataset.page;
      if (linkPage) link.classList.toggle('active', linkPage === page);
    });
    // Highlight business nav links
    document.querySelectorAll('.nav-biz-link').forEach((link) => {
      link.classList.toggle('active', page === 'business' && link.dataset.placeId === getBusinessPlaceId());
    });

    if (page === 'dashboard') loadDashboard();
    else if (page === 'favorites') loadFavoritesPage();
    else if (page === 'reviews') loadReviewsPage();
    else if (page === 'sponsors') loadSponsorsPage();
    else if (page === 'settings') loadSettingsPage();
    else if (page === 'business') {
      loadBusinessPage(getBusinessPlaceId());
      if (applyOwnerRoute) applyOwnerRoute();
    }
  };

  window.addEventListener('hashchange', () => {
    if (currentUser) navigate();
  });

  // ── Auth check ──
  show(loadingView);
  authFetch(`${AUTH_API}/auth/session/verify`, { credentials: 'include' })
    .then(r => r.json())
    .then(d => {
      if (d.ok && d.user) {
        currentUser = d.user;
        show(accountView);
        const headerUser = document.getElementById('header-user');
        const avatarSrc = d.user.avatar_url || d.user.avatar;
        headerUser.innerHTML = `<a href="/account/" class="inline-flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors" title="Account">${avatarSrc ? `<img src="${escH(avatarSrc)}" class="w-5 h-5 rounded-full object-cover">` : ''}<span>${escH(d.user.name || d.user.email)}</span></a>`;
        checkSponsors();
        // Show admin link if user is admin
        if (d.user.role === 'admin') {
          const adminLink = document.getElementById('nav-admin-link');
          if (adminLink) adminLink.classList.remove('hidden');
          if (new URLSearchParams(location.search).get('dev_destination') === 'admin') {
            location.replace('/admin/');
            return;
          }
        }
        navigate();
      } else {
        show(loginView);
      }
    })
    .catch(() => { show(loginView); });

  // ── Dashboard ──
  const loadDashboard = () => {
    if (!currentUser) return;
    const u = currentUser;

    const avatarEl = document.getElementById('dash-avatar');
    if (u.avatar_url || u.avatar) {
      avatarEl.innerHTML = `<img src="${escH(u.avatar_url || u.avatar)}" class="w-full h-full object-cover" alt="">`;
    } else {
      avatarEl.textContent = (u.name || u.email || '?').charAt(0).toUpperCase();
    }
    document.getElementById('dash-name').textContent = u.name || 'No name set';
    document.getElementById('dash-email').textContent = u.email;
    if (u.created_at) {
      document.getElementById('dash-member').textContent = `Member since ${new Date(u.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
    }

    authFetch(`${AUTH_API}/auth/favorites`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        cachedFavorites = (d.ok && d.favorites) ? d.favorites : [];
        document.getElementById('dash-fav-count').textContent = cachedFavorites.length;
        if (cachedFavorites.length > 0) {
          const f = cachedFavorites[0];
          const sec = document.getElementById('dash-recent-fav');
          sec.classList.remove('hidden');
          const href = f.slug ? `/biz/${f.slug}/` : '#';
          let html = `<a href="${href}" class="flex items-center gap-3 hover:bg-slate-50 rounded-lg transition-colors">`;
          if (f.logo_url) {
            html += `<img src="${escH(f.logo_url)}" class="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-slate-200">`;
          } else {
            html += `<div class="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600 flex-shrink-0">${escH((f.business_name || '?').charAt(0).toUpperCase())}</div>`;
          }
          html += `<div class="min-w-0 flex-1"><p class="text-sm font-medium text-slate-900 truncate">${escH(f.business_name || 'Unknown')}</p>`;
          if (f.address) html += `<p class="text-xs text-slate-500 truncate">${escH(f.address)}</p>`;
          html += '</div></a>';
          document.getElementById('dash-recent-fav-content').innerHTML = html;
        }
      });

    authFetch(`${AUTH_API}/auth/reviews/mine`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        cachedReviews = (d.ok && d.reviews) ? d.reviews : [];
        document.getElementById('dash-rev-count').textContent = cachedReviews.length;
        if (cachedReviews.length > 0) {
          const rev = cachedReviews[0];
          const sec = document.getElementById('dash-recent-rev');
          sec.classList.remove('hidden');
          let stars = '';
          for (let i = 1; i <= 5; i++) stars += `<svg class="w-3.5 h-3.5 ${i <= rev.rating ? 'text-amber-400' : 'text-slate-200'}" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.025 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z"/></svg>`;
          let html = '<div>';
          html += `<p class="text-sm font-medium text-slate-900">${escH(rev.business_name || rev.place_id)}</p>`;
          html += `<div class="flex items-center gap-1 mt-0.5 mb-1">${stars}</div>`;
          html += `<p class="text-sm text-slate-600 line-clamp-2">${escH(rev.review_text)}</p>`;
          html += '</div>';
          document.getElementById('dash-recent-rev-content').innerHTML = html;
        }
      });

    authFetch(`${AUTH_API}/auth/claims`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        cachedClaims = (d.ok && d.claims) ? d.claims : [];
        if (cachedClaims.length > 0) {
          document.getElementById('dash-claims-card').classList.remove('hidden');
          document.getElementById('dash-claims-count').textContent = cachedClaims.length;
        }
        // Populate sidebar nav with claimed businesses
        const approved = cachedClaims.filter(c => c.status === 'approved');
        const dashBusinesses = document.getElementById('dash-businesses');
        const dashBusinessesList = document.getElementById('dash-businesses-list');
        if (approved.length > 0 && dashBusinesses && dashBusinessesList) {
          dashBusinesses.classList.remove('hidden');
          dashBusinessesList.innerHTML = approved.map(c => `
            <a href="#/business/${encodeURIComponent(c.place_id)}" class="card bg-base-100 border border-green-200 p-4 flex-row items-center gap-3 hover:border-green-400 hover:shadow-sm transition-all">
              <span class="inline-flex w-10 h-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 21h16.5M4.5 21V10.5m15 10.5V10.5M3 10.5l9-7.5 9 7.5M9 21v-6a3 3 0 0 1 6 0v6"/></svg>
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-semibold text-slate-900 truncate">${escH(c.business_name || 'Your business')}</span>
                <span class="block text-xs text-green-700 mt-0.5">Verified owner</span>
              </span>
              <span class="btn btn-success btn-sm">Manage</span>
            </a>`).join('');
        } else if (dashBusinesses) {
          dashBusinesses.classList.add('hidden');
        }
        const navBiz = document.getElementById('nav-businesses');
        const navList = document.getElementById('nav-businesses-list');
        if (approved.length > 0 && navBiz && navList) {
          navBiz.classList.remove('hidden');
          navList.innerHTML = approved.map(c => {
            return `<a href="#/business/${encodeURIComponent(c.place_id)}" class="nav-link nav-biz-link" data-place-id="${escH(c.place_id)}"><svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z"/></svg><span class="truncate">${escH(c.business_name || 'Business')}</span></a>`;
          }).join('');
          // Show mobile bottom tab for first claimed business
          const mobileBizTab = document.getElementById('mobile-biz-tab');
          if (mobileBizTab) {
            mobileBizTab.href = `#/business/${encodeURIComponent(approved[0].place_id)}`;
            mobileBizTab.classList.remove('hidden');
          }
        }
      });
  };

  // ── Favorites Page ──
  const loadFavoritesPage = () => {
    const list = document.getElementById('fav-list');
    const empty = document.getElementById('fav-empty');
    const total = document.getElementById('fav-total');

    const render = (favorites) => {
      if (!favorites || favorites.length === 0) {
        empty.classList.remove('hidden');
        list.innerHTML = '';
        total.textContent = '';
        return;
      }
      empty.classList.add('hidden');
      total.textContent = `(${favorites.length})`;
      let html = '';
      favorites.forEach((f) => {
        const href = f.slug ? `/biz/${f.slug}/` : '#';
        html += `<a href="${href}" class="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-200 mb-2 hover:border-blue-300 transition-colors">`;
        if (f.logo_url) {
          html += `<img src="${escH(f.logo_url)}" alt="" class="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-slate-200">`;
        } else {
          html += `<div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600 flex-shrink-0">${escH((f.business_name || '?').charAt(0).toUpperCase())}</div>`;
        }
        html += '<div class="min-w-0 flex-1">';
        html += `<p class="text-sm font-medium text-slate-900 truncate">${escH(f.business_name || 'Unknown')}</p>`;
        if (f.address) html += `<p class="text-xs text-slate-500 truncate">${escH(f.address)}</p>`;
        else if (f.city || f.state) html += `<p class="text-xs text-slate-500">${escH(`${f.city || ''}${f.city && f.state ? ', ' : ''}${f.state || ''}`)}</p>`;
        if (f.phone) html += `<p class="text-xs text-slate-400">${escH(f.phone)}</p>`;
        html += '</div></a>';
      });
      list.innerHTML = html;
    };

    if (cachedFavorites !== null) { render(cachedFavorites); return; }
    authFetch(`${AUTH_API}/auth/favorites`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        cachedFavorites = (d.ok && d.favorites) ? d.favorites : [];
        render(cachedFavorites);
      });
  };

  // ── Reviews Page ──
  const loadReviewsPage = () => {
    const list = document.getElementById('rev-list');
    const empty = document.getElementById('rev-empty');
    const total = document.getElementById('rev-total');

    const render = (reviews) => {
      if (!reviews || reviews.length === 0) {
        empty.classList.remove('hidden');
        list.innerHTML = '';
        total.textContent = '';
        return;
      }
      empty.classList.add('hidden');
      total.textContent = `(${reviews.length})`;
      let html = '';
      reviews.forEach((rev) => {
        let stars = '';
        for (let i = 1; i <= 5; i++) {
          stars += `<svg class="w-4 h-4 ${i <= rev.rating ? 'text-amber-400' : 'text-slate-300'}" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.025 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z"/></svg>`;
        }
        const date = rev.created_at ? new Date(rev.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
        let photos = [];
        try { photos = JSON.parse(rev.photos || '[]'); } catch(e) {}
        let statusBadge = '';
        if (rev.status && rev.status !== 'approved') {
          statusBadge = `<span class="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">${escH(rev.status)}</span>`;
        }

        // Find slug from favorites cache
        const revFav = (cachedFavorites || []).filter(f => f.place_id === rev.place_id)[0];
        const revSlug = revFav ? revFav.slug : null;
        const bizLabel = rev.business_name || rev.place_id;
        const bizNameHtml = revSlug
          ? `<a href="/biz/${escH(revSlug)}/" class="text-sm font-semibold text-slate-900 hover:text-blue-600 transition-colors">${escH(bizLabel)}</a>`
          : `<span class="text-sm font-semibold text-slate-900 rev-biz-name" data-place-id="${escH(rev.place_id)}">${escH(bizLabel)}</span>`;

        html += `<div class="card bg-base-100 border border-base-300 p-4" id="acct-review-${rev.id}">`;
        html += '<div class="flex items-start justify-between mb-2">';
        html += `<div>${bizNameHtml}`;
        html += `<div class="flex items-center gap-1 mt-0.5">${stars}</div></div>`;
        html += `<div class="flex items-center gap-2">${statusBadge}`;
        if (date) html += `<span class="text-xs text-slate-400">${date}</span>`;
        html += '</div></div>';
        html += `<p class="text-sm text-slate-700 mb-2">${escH(rev.review_text)}</p>`;

        if (photos.length > 0) {
          html += '<div class="flex gap-2 mb-2">';
          photos.forEach((url) => {
            html += `<img src="${escH(url)}" class="w-16 h-16 object-cover rounded-lg border border-slate-200" loading="lazy">`;
          });
          html += '</div>';
        }
        html += `<button data-review-id="${rev.id}" class="acct-review-delete text-xs text-red-500 hover:text-red-700 transition-colors">Delete</button>`;
        html += '</div>';
      });
      list.innerHTML = html;

      // Resolve slugs for business names that aren't linked yet
      list.querySelectorAll('.rev-biz-name').forEach((el) => {
        const pid = el.getAttribute('data-place-id');
        if (!pid) return;
        authFetch(`${AUTH_API}/auth/claims/check?place_id=${encodeURIComponent(pid)}`)
          .then(r => r.json())
          .then(d => {
            if (d.biz_slug) {
              const a = document.createElement('a');
              a.href = `/biz/${d.biz_slug}/`;
              a.className = 'text-sm font-semibold text-slate-900 hover:text-blue-600 transition-colors';
              a.textContent = el.textContent;
              el.replaceWith(a);
            }
          })
          .catch(() => {});
      });

      list.querySelectorAll('.acct-review-delete').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.getAttribute('data-review-id');
          showConfirm('Delete this review?', { confirmText: 'Delete' }).then(ok => {
            if (!ok) return;
            btn.disabled = true; btn.textContent = 'Deleting...';
            authFetch(`${AUTH_API}/auth/reviews/${id}`, { method: 'DELETE', credentials: 'include' })
              .then(r => r.json())
              .then(d => {
                if (d.ok) {
                  const row = document.getElementById(`acct-review-${id}`);
                  if (row) row.remove();
                  cachedReviews = cachedReviews.filter(r => r.id != id);
                  if (!cachedReviews.length) { empty.classList.remove('hidden'); total.textContent = ''; }
                  else total.textContent = `(${cachedReviews.length})`;
                } else {
                  btn.disabled = false; btn.textContent = 'Delete';
                  showAlert(d.error || 'Failed to delete', 'error');
                }
              })
              .catch(() => { btn.disabled = false; btn.textContent = 'Delete'; });
          });
        });
      });
    };

    if (cachedReviews !== null) { render(cachedReviews); return; }
    authFetch(`${AUTH_API}/auth/reviews/mine`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        cachedReviews = (d.ok && d.reviews) ? d.reviews : [];
        render(cachedReviews);
      });
  };

  // ── Sponsorships Page ──
  let sponsorsLoaded = false;
  const loadSponsorsPage = () => {
    if (!currentUser || sponsorsLoaded) return;
    const list = document.getElementById('sponsors-list');
    if (!list) return;

    authFetch(`/sponsors/my-sponsors?email=${encodeURIComponent(currentUser.email)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.ok || !d.sponsors || d.sponsors.length === 0) {
          list.innerHTML = `<div class="text-center py-8">
            <p class="text-slate-500 text-sm mb-2">No sponsored listings yet.</p>
            <a href="/search/" class="text-sm font-medium text-blue-600 hover:underline">Choose a listing to sponsor</a>
          </div>`;
          return;
        }

        list.innerHTML = d.sponsors.map(s => {
          const parts = s.slot.split(':');
          let slotLabel = s.slot;
          if (parts.length === 2) {
            const svc = parts[0].replace(/-/g, ' ').replace(/\bgeneral\b/i, '').trim().replace(/\b\w/g, c => c.toUpperCase());
            const m = parts[1].match(/^(.+)-([a-z]{2})$/);
            const city = m ? parts[1].slice(0, -3).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : parts[1];
            const state = m ? m[2].toUpperCase() : '';
            slotLabel = `${svc} in ${city}${state ? ', ' + state : ''}`;
          }
          const statusBadge = s.active
            ? '<span class="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Active</span>'
            : '<span class="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Inactive</span>';
          const manageLink = s.manage_url
            ? `<a href="${escH(s.manage_url)}" class="text-xs font-medium text-blue-600 hover:underline">Manage</a>`
            : '';
          const listingUrl = parts.length === 2 ? `/best-${parts[0]}-${parts[1]}/` : '#';

          return `<div class="card bg-base-100 border border-base-300 p-4">
            <div class="flex items-center justify-between mb-1">
              <p class="font-semibold text-slate-900 text-sm">${escH(s.name)}</p>
              ${statusBadge}
            </div>
            <p class="text-xs text-slate-500 mb-2">${escH(slotLabel)}</p>
            <div class="flex items-center gap-3">
              ${manageLink}
              <a href="${escH(listingUrl)}" class="text-xs text-slate-400 hover:text-slate-600 hover:underline">View listing page</a>
            </div>
          </div>`;
        }).join('');

        sponsorsLoaded = true;
      })
      .catch(() => {
        list.innerHTML = '<p class="text-sm text-slate-400">Unable to load sponsorships.</p>';
      });
  };

  // Check for sponsorships on auth (to show/hide nav item)
  const checkSponsors = () => {
    if (!currentUser) return;
    authFetch(`/sponsors/my-sponsors?email=${encodeURIComponent(currentUser.email)}`)
      .then(r => r.json())
      .then(d => {
        if (d.ok && d.sponsors && d.sponsors.length > 0) {
          const navEl = document.getElementById('nav-sponsors');
          if (navEl) navEl.classList.remove('hidden');
        }
      })
      .catch(() => {});
  };

  // ── Settings Page ──
  const loadSettingsPage = () => {
    if (!currentUser) return;
    document.getElementById('edit-name-input').value = currentUser.name || '';
    loadClaims();
  };

  const loadClaims = () => {
    const render = (claims) => {
      if (!claims || claims.length === 0) return;
      const section = document.getElementById('claims-section');
      const list = document.getElementById('claims-list');
      section.classList.remove('hidden');
      document.getElementById('claims-count').textContent = `(${claims.length})`;

      let html = '';
      claims.forEach((c) => {
        const statusColor = c.status === 'approved' ? 'green' : c.status === 'pending' ? 'amber' : 'red';
        html += `<div class="p-3 border-b border-slate-100 last:border-0" id="claim-row-${c.id}">`;
        html += '<div class="flex items-center justify-between">';
        html += `<div class="min-w-0"><p class="text-sm font-medium text-slate-900 truncate">${escH(c.business_name || c.place_id)}</p></div>`;
        html += '<div class="flex items-center gap-2 flex-shrink-0">';
        if (c.status === 'pending' && (c.verify_domain || c.contact_email || c.phone)) {
          html += `<button data-claim-id="${c.id}" data-domain="${escH(c.verify_domain || '')}" data-contact-email="${escH(c.contact_email || '')}" data-phone="${escH(c.phone || '')}" class="claim-verify-btn text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors">Verify</button>`;
        }
        html += `<span class="text-xs font-medium px-2 py-0.5 rounded-full bg-${statusColor}-100 text-${statusColor}-700">${escH(c.status)}</span>`;
        html += `<button data-claim-id="${c.id}" class="claim-remove-btn text-xs text-slate-400 hover:text-red-600 transition-colors" title="Remove claim">&#10005;</button>`;
        html += '</div></div>';
        // Vanity handle for approved claims
        if (c.status === 'approved') {
          const currentSlug = c.vanity_slug || '';
          html += '<div class="flex items-center gap-2 mt-2 pl-1">';
          html += '<span class="text-xs text-slate-400">prosource.app/</span>';
          html += `<input type="text" class="vanity-input text-xs border border-slate-200 rounded px-2 py-1 w-32 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none" data-claim-id="${c.id}" value="${escH(currentSlug)}" placeholder="@yourhandle" maxlength="30">`;
          html += `<button data-claim-id="${c.id}" class="vanity-save-btn text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors">Save</button>`;
          if (currentSlug) {
            html += `<a href="/@${escH(currentSlug)}" target="_blank" class="text-xs text-slate-400 hover:text-blue-600 transition-colors" title="Test your link">Test</a>`;
          }
          html += '</div>';
        }
        html += '</div>';
      });
      list.innerHTML = html;

      list.querySelectorAll('.claim-verify-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          showClaimVerifyModal(
            btn.getAttribute('data-claim-id'),
            btn.getAttribute('data-domain'),
            btn.getAttribute('data-contact-email'),
            btn.getAttribute('data-phone')
          );
        });
      });

      list.querySelectorAll('.claim-remove-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.getAttribute('data-claim-id');
          showConfirm('Remove this business claim?', { confirmText: 'Remove' }).then(ok => {
            if (!ok) return;
            btn.disabled = true; btn.textContent = '...';
            authFetch(`${AUTH_API}/auth/claims/${id}`, { method: 'DELETE', credentials: 'include' })
              .then(r => r.json())
              .then(d => {
                if (d.ok) {
                  const row = document.getElementById(`claim-row-${id}`);
                  if (row) row.remove();
                  if (!list.children.length) section.classList.add('hidden');
                } else {
                  btn.disabled = false; btn.textContent = '\u2715';
                  showAlert(d.error || 'Failed to remove claim', 'error');
                }
              })
              .catch(() => { btn.disabled = false; btn.textContent = '\u2715'; });
          });
        });
      });

      // Vanity handle save
      list.querySelectorAll('.vanity-save-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const claimId = btn.getAttribute('data-claim-id');
          const input = list.querySelector(`.vanity-input[data-claim-id="${claimId}"]`);
          let slug = (input?.value || '').trim().toLowerCase().replace(/^@/, '');

          if (!slug) {
            // Remove vanity
            authFetch(`${AUTH_API}/auth/claims/vanity/${claimId}`, { method: 'DELETE', credentials: 'include' })
              .then(r => r.json())
              .then(d => {
                if (d.ok) showAlert('Handle removed', 'success');
                else showAlert(d.error || 'Failed', 'error');
              });
            return;
          }

          btn.textContent = '...';
          authFetch(`${AUTH_API}/auth/claims/vanity`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ claim_id: claimId, slug }),
          })
            .then(r => r.json())
            .then(d => {
              btn.textContent = 'Save';
              if (d.ok) {
                input.value = slug;
                showAlert(`Handle set: prosource.app/@${slug}`, 'success');
                // Update cached claims
                const claim = cachedClaims?.find(c => c.id === claimId);
                if (claim) claim.vanity_slug = slug;
                // Refresh to show test link
                render(cachedClaims);
              } else {
                showAlert(d.error || 'Failed to set handle', 'error');
              }
            })
            .catch(() => { btn.textContent = 'Save'; });
        });
      });
    };

    if (cachedClaims !== null) { render(cachedClaims); return; }
    authFetch(`${AUTH_API}/auth/claims`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        cachedClaims = (d.ok && d.claims) ? d.claims : [];
        render(cachedClaims);
      });
  };

  // ── Manage Business Page ──
  let bizPanelLoaded = null; // place_id of last loaded panel

  const loadBusinessPage = (placeId) => {
    if (!placeId) return;
    managePlaceId = placeId;

    // Find business name from claims, then favorites
    const claim = (cachedClaims || []).filter(c => c.place_id === placeId)[0];
    const fav = (cachedFavorites || []).filter(f => f.place_id === placeId)[0];
    manageBizName = (claim && claim.business_name && claim.business_name.indexOf('ChIJ') !== 0) ? claim.business_name
      : (fav && fav.business_name) ? fav.business_name : '';
    const nameEl = document.getElementById('manage-biz-name');
    if (nameEl) nameEl.textContent = manageBizName || 'Manage Business';

    // Set "View Profile" link
    const bizLink = document.getElementById('manage-biz-link');
    if (bizLink) {
      if (fav && fav.slug) {
        bizLink.href = `/biz/${fav.slug}/`;
      } else {
        bizLink.href = `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeId)}`;
      }
      ownerProfileUrl = new URL(bizLink.getAttribute('href'), location.origin).href;
    }

    // Resolve slug + name from KV if not found yet
    authFetch(`${AUTH_API}/auth/claims/check?place_id=${encodeURIComponent(placeId)}`)
      .then(r => r.json())
      .then(d => {
        if (d.biz_slug && bizLink) {
          bizLink.href = `/biz/${d.biz_slug}/`;
          ownerProfileUrl = new URL(bizLink.getAttribute('href'), location.origin).href;
        }
        // If we still don't have a good name, try to get it from page data
        if (!manageBizName || manageBizName === 'Manage Business') {
          if (d.biz_slug) {
            // Business name can be derived from the slug as a last resort
            const readable = d.biz_slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            manageBizName = readable;
            if (nameEl) nameEl.textContent = readable;
          }
        }
      })
      .catch(() => {});

    // Show the owner panel
    const panel = document.getElementById('owner-panel');
    if (panel) panel.style.display = '';

    // Only init once per place_id
    if (bizPanelLoaded === placeId) {
      if (applyOwnerRoute) applyOwnerRoute();
      return;
    }
    bizPanelLoaded = placeId;

    const stepGuides = {
      info: {
        number: 1, title: 'Confirm your business details',
        instructions: 'Review the contact information below, correct anything that is outdated, then click “Save Changes.” Saving confirms this step even when the details are already correct.',
      },
      photos: {
        number: 2, title: 'Show customers the work you do',
        instructions: 'Upload at least 3 clear project photos. You can select several photos at once. This step completes automatically after the third upload.',
      },
      trust: {
        number: 3, title: 'Add reasons customers can trust you',
        instructions: 'Select at least one trust badge that truthfully applies to your business, then click “Save Settings” at the bottom of this section.',
      },
    };

    const setWizardFocus = (focused, stepKey = null) => {
      document.querySelectorAll('.wizard-hidden-by-setup').forEach((el) => {
        el.classList.remove('wizard-hidden-by-setup');
        el.style.removeProperty('display');
        if (el.dataset.wizardPreviousDisplay) el.style.display = el.dataset.wizardPreviousDisplay;
        delete el.dataset.wizardPreviousDisplay;
      });
      if (!focused) return;

      const hide = (el) => {
        if (!el || el.classList.contains('wizard-hidden-by-setup')) return;
        el.dataset.wizardPreviousDisplay = el.style.display || '';
        el.classList.add('wizard-hidden-by-setup');
        el.style.setProperty('display', 'none', 'important');
      };
      [
        document.getElementById('owner-overview'),
        document.getElementById('owner-tool-groups'),
        document.getElementById('owner-tools-summary'),
        document.getElementById('owner-tabs'),
        document.getElementById('owner-tabs-grid'),
        document.getElementById('owner-tab-back'),
        document.getElementById('owner-dashboard-heading'),
        document.getElementById('owner-verified-blurb'),
        document.getElementById('manage-business-header'),
        document.getElementById('mobile-tab-bar'),
      ].forEach(hide);

      if (stepKey === 'trust') {
        document.querySelectorAll('#owner-tab-settings > .space-y-4 > *').forEach((el) => {
          if (!el.querySelector('#badge-checkboxes') && !el.querySelector('#owner-settings-save')) hide(el);
        });
      }
    };

    const openOwnerTab = (name, stepKey = null, updateRoute = true) => {
      if (updateRoute) {
        const suffix = stepKey ? `/setup/${encodeURIComponent(stepKey)}` : `/tool/${encodeURIComponent(name)}`;
        history.pushState(null, '', `#/business/${encodeURIComponent(placeId)}${suffix}`);
      }
      setWizardFocus(true, stepKey);
      const focusHeader = document.getElementById('owner-focus-header');
      const focusTitle = document.getElementById('owner-focus-title');
      const toolTitles = {
        jobs: 'Jobs', bookings: 'Bookings', estimates: 'Estimates', leads: 'Leads',
        reviews: 'Reviews & requests', edit: 'Business details', photos: 'Photos',
        portfolio: 'Project portfolio', settings: 'Profile settings',
      };
      if (focusHeader) {
        focusHeader.classList.remove('hidden');
        focusHeader.classList.add('flex');
      }
      if (focusTitle) focusTitle.textContent = stepKey ? 'Profile setup' : (toolTitles[name] || 'Business tool');
      const tools = document.getElementById('owner-tools');
      if (tools) tools.open = true;
      const guide = document.getElementById('owner-step-guide');
      const guideData = stepKey ? stepGuides[stepKey] : null;
      if (guide) guide.classList.toggle('hidden', !guideData);
      if (guideData) {
        guide.dataset.step = stepKey;
        guide.className = 'rounded-xl border-2 border-blue-300 bg-blue-50 p-4 mb-3';
        document.getElementById('owner-step-eyebrow').textContent = `Profile setup ${guideData.number} of 3`;
        document.getElementById('owner-step-title').textContent = guideData.title;
        document.getElementById('owner-step-instructions').textContent = guideData.instructions;
      }
      document.querySelectorAll('.owner-tab-content').forEach((c) => { c.style.display = 'none'; });
      const target = document.getElementById(`owner-tab-${name}`);
      if (target) target.style.display = '';
      document.querySelectorAll('.owner-tool-link').forEach((link) => {
        const active = link.getAttribute('data-open-owner-tab') === name;
        link.classList.toggle('bg-slate-100', active);
        link.classList.toggle('font-semibold', active);
        link.classList.toggle('text-slate-900', active);
      });
      const desktopTabs = document.getElementById('owner-tabs');
      if (desktopTabs) desktopTabs.querySelectorAll('.tab').forEach((t) => {
        t.classList.toggle('tab-active', t.getAttribute('data-tab') === name);
      });
      if (window.innerWidth < 640) {
        const grid = document.getElementById('owner-tabs-grid');
        const back = document.getElementById('owner-tab-back');
        if (grid) grid.style.display = 'none';
        if (back) back.style.display = '';
      }
      target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    document.querySelectorAll('[data-open-owner-tab]').forEach((button) => {
      button.onclick = () => openOwnerTab(button.getAttribute('data-open-owner-tab'), button.getAttribute('data-step'));
    });

    const focusBack = document.getElementById('owner-focus-back');
    if (focusBack) focusBack.onclick = () => {
      location.hash = `#/business/${encodeURIComponent(placeId)}`;
    };

    const showOwnerOverview = () => {
      setWizardFocus(false);
      const focusHeader = document.getElementById('owner-focus-header');
      if (focusHeader) {
        focusHeader.classList.add('hidden');
        focusHeader.classList.remove('flex');
      }
      const tools = document.getElementById('owner-tools');
      if (tools) tools.open = false;
      document.querySelectorAll('.owner-tab-content').forEach((content) => { content.style.display = 'none'; });
      document.querySelectorAll('.owner-tool-link').forEach((link) => link.classList.remove('bg-slate-100', 'font-semibold', 'text-slate-900'));
    };

    applyOwnerRoute = () => {
      const route = getBusinessRoute();
      if (!route || route.placeId !== placeId || route.kind === 'overview') return showOwnerOverview();
      if (route.kind === 'tool' && route.value) return openOwnerTab(route.value, null, false);
      if (route.kind === 'setup' && route.value && stepGuides[route.value]) {
        const tabByStep = { info: 'edit', photos: 'photos', trust: 'settings' };
        return openOwnerTab(tabByStep[route.value], route.value, false);
      }
      showOwnerOverview();
    };

    initProfileSharing();

    // Tab switching — desktop tabs
    const tabs = document.getElementById('owner-tabs');
    if (tabs) {
      tabs.onclick = (e) => {
        const tab = e.target.closest('.tab');
        if (!tab) return;
        const name = tab.getAttribute('data-tab');
        tabs.querySelectorAll('.tab').forEach((t) => { t.classList.remove('tab-active'); });
        tab.classList.add('tab-active');
        openOwnerTab(name);
      };
    }

    // Tab switching — mobile grid
    // Replaced by the grouped responsive tool navigation above.
    const tabsGrid = null;
    const tabBack = document.getElementById('owner-tab-back');
    const showMobileTab = (name) => {
      // Hide grid, show tab content + back button
      if (tabsGrid) tabsGrid.style.display = 'none';
      if (tabBack) tabBack.style.display = '';
      document.querySelectorAll('.owner-tab-content').forEach((c) => { c.style.display = 'none'; });
      const target = document.getElementById(`owner-tab-${name}`);
      if (target) target.style.display = '';
      // Also sync desktop tabs
      if (tabs) {
        tabs.querySelectorAll('.tab').forEach((t) => { t.classList.toggle('tab-active', t.getAttribute('data-tab') === name); });
      }
    };
    const showMobileGrid = () => {
      if (tabsGrid) tabsGrid.style.display = '';
      if (tabBack) tabBack.style.display = 'none';
      // On mobile, hide all tab content when showing grid
      document.querySelectorAll('.owner-tab-content').forEach((c) => { c.style.display = 'none'; });
    };

    if (tabsGrid) {
      tabsGrid.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-tab]');
        if (!btn) return;
        showMobileTab(btn.getAttribute('data-tab'));
      });
    }
    if (tabBack) {
      tabBack.addEventListener('click', () => showMobileGrid());
    }

    // The activation overview is the landing state on every screen size.
    const isMobile = window.innerWidth < 640;
    if (isMobile) {
      showMobileGrid();
    } else {
      if (tabs) tabs.querySelectorAll('.tab').forEach((t) => { t.classList.remove('tab-active'); });
      document.querySelectorAll('.owner-tab-content').forEach((c) => { c.style.display = 'none'; });
    }

    // Load data
    authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.ok) return;
        populateEditForm(d.edits || {}, placeId);
        renderOwnerPhotos(d.photos || [], placeId);
        refreshOwnerActivation(placeId, d.edits || {}, d.photos || []);
      });

    initEditForm(placeId);
    initPhotoUpload(placeId);
    loadOwnerLeads(placeId);
    loadOwnerPortfolio(placeId);
    loadOwnerSettings(placeId);
    loadCommunityReviews(placeId);
    initReviewRequestForm(placeId);
    loadJobsPipeline(placeId);
    loadBookings(placeId);
    loadEstimates(placeId);
    applyOwnerRoute();
  };

  const initProfileSharing = () => {
    const message = document.getElementById('owner-share-msg');
    const showMessage = (text) => {
      if (!message) return;
      message.textContent = text;
      message.classList.remove('hidden');
      setTimeout(() => message.classList.add('hidden'), 3000);
    };
    const getUrl = () => ownerProfileUrl || document.getElementById('manage-biz-link')?.href || location.origin;
    const copy = document.getElementById('owner-copy-profile');
    if (copy) copy.onclick = () => navigator.clipboard.writeText(getUrl()).then(() => showMessage('Profile link copied.'));
    const share = document.getElementById('owner-share-profile');
    if (share) share.onclick = () => {
      const data = { title: manageBizName || 'Our ProSource profile', text: `See ${manageBizName || 'our business'} on ProSource`, url: getUrl() };
      if (navigator.share) navigator.share(data).catch(() => {});
      else navigator.clipboard.writeText(getUrl()).then(() => showMessage('Profile link copied.'));
    };
    const qr = document.getElementById('owner-download-qr');
    if (qr) qr.onclick = () => {
      QRCode.toDataURL(getUrl(), { width: 720, margin: 3, color: { dark: '#0f172a', light: '#ffffff' } })
        .then((dataUrl) => {
          const a = document.createElement('a');
          a.href = dataUrl;
          a.download = `${(manageBizName || 'prosource-profile').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-qr.png`;
          a.click();
          showMessage('QR code downloaded.');
        })
        .catch(() => showMessage('Unable to create QR code.'));
    };
  };

  const refreshOwnerActivation = (placeId, edits = {}, photos = []) => {
    authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/settings`).then(r => r.json()).catch(() => ({ settings: {} })).then((settingsData) => {
      const settings = settingsData.settings || {};
      let badges = [];
      try { badges = JSON.parse(settings.badges || '[]'); } catch (_) {}
      const state = {
        info: settings.profile_confirmed === 'on' || Boolean(edits.phone && edits.website && edits.description),
        photos: photos.length >= 3,
        trust: badges.length > 0,
      };
      const order = [
        ['info', 'edit', 'Confirm your business details'],
        ['photos', 'photos', 'Add 3 project photos'],
        ['trust', 'settings', 'Add trust details'],
      ];
      const completed = order.filter(([key]) => state[key]).length;
      const percent = Math.round((completed / order.length) * 100);
      const ring = document.getElementById('owner-progress-ring');
      const bar = document.getElementById('owner-progress-bar');
      const copy = document.getElementById('owner-progress-copy');
      if (ring) { ring.style.setProperty('--value', String(percent)); ring.textContent = `${percent}%`; }
      if (bar) bar.value = percent;
      if (copy) copy.textContent = completed === 3 ? 'Your profile is customer-ready.' : `${completed} of 3 setup tasks complete`;
      order.forEach(([key]) => {
        const row = document.querySelector(`.owner-check-step[data-step="${key}"]`);
        const icon = row?.querySelector('.owner-check-icon');
        if (!row || !icon) return;
        row.classList.toggle('border-green-300', state[key]);
        row.classList.toggle('bg-green-50', state[key]);
        icon.className = `owner-check-icon inline-flex w-5 h-5 items-center justify-center rounded-full text-xs mr-2 ${state[key] ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-400'}`;
        icon.textContent = state[key] ? '✓' : String(order.findIndex(([k]) => k === key) + 1);
      });
      const activeGuide = document.getElementById('owner-step-guide');
      if (activeGuide && !activeGuide.classList.contains('hidden') && state[activeGuide.dataset.step]) {
        activeGuide.className = 'rounded-xl border-2 border-green-300 bg-green-50 p-4 mb-3';
        document.getElementById('owner-step-eyebrow').textContent = 'Step complete';
        document.getElementById('owner-step-title').textContent = 'Nice work — your profile is stronger';
        document.getElementById('owner-step-instructions').textContent = 'Return to setup to continue with the next recommended step.';
      }
      const next = order.find(([key]) => !state[key]);
      const button = document.getElementById('owner-next-action');
      if (button && next) {
        button.style.display = '';
        button.textContent = next[2];
        button.setAttribute('data-open-owner-tab', next[1]);
        button.onclick = () => {
          document.querySelector(`.owner-check-step[data-step="${next[0]}"]`)?.click();
        };
      } else if (button) {
        button.style.display = 'none';
      }
    });
  };

  // ── Edit Info Tab ──
  const populateEditForm = (edits, placeId) => {
    const form = document.getElementById('owner-edit-form');
    if (!form) return;
    let defaultName = manageBizName || '';
    // Don't use place_id as a name fallback
    if (defaultName.indexOf('ChIJ') === 0) defaultName = '';
    form.elements.name.value = edits.name || defaultName;
    form.elements.phone.value = edits.phone || '';
    form.elements.website.value = edits.website || '';
    form.elements.address.value = edits.address || '';
    form.elements.description.value = edits.description || '';
  };

  const initEditForm = (placeId) => {
    const form = document.getElementById('owner-edit-form');
    if (!form) return;
    form.onsubmit = (e) => {
      e.preventDefault();
      const data = {};
      ['name', 'phone', 'website', 'address', 'description'].forEach((f) => {
        data[f] = form.elements[f].value.trim();
      });
      const msg = document.getElementById('owner-edit-msg');
      msg.className = 'text-sm'; msg.textContent = 'Saving...'; msg.classList.remove('hidden');
      authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })
        .then(r => r.json())
        .then(d => {
          msg.className = d.ok ? 'text-sm text-green-700' : 'text-sm text-red-600';
          msg.textContent = d.ok ? 'Changes saved!' : (d.error || 'Save failed');
          if (d.ok) {
            authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/settings`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ profile_confirmed: 'on' })
            }).then(() => refreshOwnerActivation(placeId, data, ownerPhotos));
          }
          setTimeout(() => { msg.classList.add('hidden'); }, 3000);
        });
    };
  };

  // ── Photos Tab ──
  let ownerPhotos = [];
  let scrapedPhotos = []; // { url, hidden }
  let hiddenUrls = {};


  const renderOwnerPhotos = (photos, placeId) => {
    ownerPhotos = photos;
    // Load hidden list + scraped gallery, then render everything
    Promise.all([
      authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/hidden-images`).then(r => r.json()).catch(() => ({ ok: false })),
      authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/gallery`).then(r => r.json()).catch(() => ({ ok: false }))
    ]).then((results) => {
      hiddenUrls = {};
      if (results[0].ok && results[0].urls) results[0].urls.forEach((u) => { hiddenUrls[u] = true; });
      scrapedPhotos = (results[1].ok && results[1].images) ? results[1].images.map((url) => ({ url: url, hidden: !!hiddenUrls[url] })) : [];
      renderAllPhotos(placeId);
    });
  };

  const renderAllPhotos = (placeId) => {
    const grid = document.getElementById('owner-photos-grid');
    const countEl = document.getElementById('owner-photo-count');
    const upload = document.getElementById('owner-photo-upload');
    if (!grid) return;

    const visibleCount = ownerPhotos.length + scrapedPhotos.filter(p => !p.hidden).length;
    if (countEl) countEl.textContent = ownerPhotos.length;
    if (upload) upload.style.display = ownerPhotos.length >= 20 ? 'none' : '';

    let html = '';

    // Scraped photos section
    if (scrapedPhotos.length > 0) {
      html += '<p class="text-xs font-semibold text-slate-400 uppercase tracking-wider col-span-full mb-1">Scraped Photos</p>';
      scrapedPhotos.forEach((p) => {
        const isHidden = p.hidden;
        html += `<div class="relative group${isHidden ? ' opacity-40' : ''}" data-scraped-url="${escH(p.url)}">`;
        html += `<img src="${escH(p.url)}" alt="Gallery photo" class="w-full h-24 sm:h-28 object-cover rounded-lg border border-slate-200">`;
        if (isHidden) {
          html += '<div class="absolute inset-0 flex items-center justify-center">';
          html += `<button type="button" data-url="${escH(p.url)}" class="scraped-unhide btn btn-xs btn-success">Show</button>`;
          html += '</div>';
        } else {
          html += `<button type="button" data-url="${escH(p.url)}" class="scraped-hide absolute top-1 right-1 btn btn-xs btn-error opacity-0 group-hover:opacity-100 transition-opacity">Hide</button>`;
        }
        html += '</div>';
      });
    }

    // Owner-uploaded photos section
    if (ownerPhotos.length > 0 || scrapedPhotos.length > 0) {
      html += '<p class="text-xs font-semibold text-slate-400 uppercase tracking-wider col-span-full mb-1 mt-3">Your Uploads</p>';
    }
    if (ownerPhotos.length === 0) {
      html += `<div class="col-span-full">${ownerEmptyState('Add your first business photos', 'Upload clear examples of your work, team, or location so customers know what to expect.')}</div>`;
    } else {
      ownerPhotos.forEach((p) => {
        html += '<div class="relative group">';
        html += `<img src="${escH(p.url)}" alt="Owner photo" class="w-full h-24 sm:h-28 object-cover rounded-lg border border-slate-200">`;
        html += `<button type="button" data-photo-id="${escH(p.id)}" class="owner-photo-del absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" title="Delete">&times;</button>`;
        html += '</div>';
      });
    }

    grid.innerHTML = html;

    // Hide scraped photo handler
    grid.querySelectorAll('.scraped-hide').forEach((btn) => {
      btn.addEventListener('click', () => {
        const url = btn.getAttribute('data-url');
        const card = btn.closest('[data-scraped-url]');
        if (card) { card.style.transition = 'opacity 0.3s'; card.style.opacity = '0.4'; }
        hiddenUrls[url] = true;
        scrapedPhotos.forEach((p) => { if (p.url === url) p.hidden = true; });
        setTimeout(() => { renderAllPhotos(placeId); }, 300);
        authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/hidden-images`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: url })
        });
      });
    });

    // Unhide scraped photo handler
    grid.querySelectorAll('.scraped-unhide').forEach((btn) => {
      btn.addEventListener('click', () => {
        const url = btn.getAttribute('data-url');
        delete hiddenUrls[url];
        scrapedPhotos.forEach((p) => { if (p.url === url) p.hidden = false; });
        renderAllPhotos(placeId);
        authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/hidden-images`, {
          method: 'DELETE', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: url })
        });
      });
    });

    // Delete owner photo handler
    grid.querySelectorAll('.owner-photo-del').forEach((btn) => {
      btn.addEventListener('click', () => {
        const photoId = btn.getAttribute('data-photo-id');
        const card = btn.closest('.relative');
        if (card) { card.style.transition = 'opacity 0.3s, transform 0.3s'; card.style.opacity = '0'; card.style.transform = 'scale(0.9)'; }
        ownerPhotos = ownerPhotos.filter(p => p.id !== photoId);
        setTimeout(() => { renderAllPhotos(placeId); }, 300);
        authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/photos/${photoId}`, { method: 'DELETE' });
      });
    });
  };

  const initPhotoUpload = (placeId) => {
    const input = document.getElementById('owner-photo-input');
    const dropZone = document.getElementById('owner-tab-photos');
    if (!input) return;

    // Allow multiple files
    input.setAttribute('multiple', '');

    // Handle file input change (multi-select)
    input.onchange = () => {
      const files = Array.from(input.files || []);
      input.value = '';
      uploadMultiplePhotos(files, placeId);
    };

    // Drag and drop on the photos tab
    if (dropZone) {
      dropZone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropZone.classList.add('ring-2', 'ring-green-400', 'ring-inset');
      });
      dropZone.addEventListener('dragleave', (e) => {
        e.preventDefault();
        dropZone.classList.remove('ring-2', 'ring-green-400', 'ring-inset');
      });
      dropZone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropZone.classList.remove('ring-2', 'ring-green-400', 'ring-inset');
        const files = Array.from(e.dataTransfer.files || []);
        uploadMultiplePhotos(files, placeId);
      });
    }
  };

  const uploadMultiplePhotos = (files, placeId) => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    let valid = files.filter(f => allowed.indexOf(f.type) !== -1);
    if (valid.length === 0) return;

    const remaining = 20 - ownerPhotos.length;
    if (remaining <= 0) { showAlert('Maximum 20 photos allowed.', 'info'); return; }
    valid = valid.slice(0, remaining);

    const grid = document.getElementById('owner-photos-grid');
    if (!grid) return;

    valid.forEach((file) => {
      const progress = createProgressBar(grid, file);

      authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/photos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_type: file.type })
      })
        .then(r => r.json())
        .then(d => {
          if (!d.ok) { progress.error(d.error || 'Upload failed'); return; }
          return uploadWithProgress(d.upload_url, file, file.type, (pct) => {
            progress.update(pct);
          }).then(() => {
            ownerPhotos.push({ id: d.photo_id, url: d.cdn_url, sort_order: ownerPhotos.length });
            progress.done(d.cdn_url);
            refreshOwnerActivation(placeId, {}, ownerPhotos);
            // Re-render grid after a short delay to let the check animation play
            setTimeout(() => { renderOwnerPhotos(ownerPhotos, placeId); }, 1300);
          });
        })
        .catch(() => { progress.error('Network error'); });
    });
  };

  // ── Responses Tab ──

  const ownerEmptyState = (title, description, action = '') => `
    <div class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-10 sm:py-12 text-center">
      <div class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm ring-1 ring-slate-200">
        <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h7.5M8.25 10.5h7.5m-7.5 3.75h4.5M6.75 3.75h10.5A2.25 2.25 0 0 1 19.5 6v12a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 18V6a2.25 2.25 0 0 1 2.25-2.25Z"/></svg>
      </div>
      <p class="text-base font-semibold text-slate-900">${title}</p>
      <p class="mx-auto mt-1 max-w-md text-sm leading-6 text-slate-500">${description}</p>
      ${action ? `<div class="mt-5">${action}</div>` : ''}
    </div>`;


  // ── Leads Tab ──
  const loadOwnerLeads = (placeId) => {
    const list = document.getElementById('owner-leads-list');
    if (!list) return;
    list.innerHTML = '<p class="text-sm text-slate-400">Loading leads...</p>';

    authFetch(`${AUTH_API}/api/leads/${encodeURIComponent(placeId)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.ok || !d.leads || d.leads.length === 0) {
          list.innerHTML = ownerEmptyState(
            'Your first lead will appear here',
            'Customers can request a quote from your public business profile. Share the profile to help the right people find you.',
            `<a class="btn btn-primary btn-sm" href="${escH(ownerProfileUrl)}" target="_blank" rel="noopener">View public profile</a>`,
          );
          return;
        }
        const statusColors = { new: 'bg-blue-100 text-blue-700', contacted: 'bg-amber-100 text-amber-700', won: 'bg-green-100 text-green-700', lost: 'bg-slate-100 text-slate-500' };
        let html = '';
        d.leads.forEach((lead) => {
          const color = statusColors[lead.status] || statusColors.new;
          const date = lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
          html += '<div class="bg-white border border-slate-200 rounded-lg p-3">';
          html += '<div class="flex items-center justify-between mb-2">';
          html += `<div class="flex items-center gap-2"><span class="font-semibold text-sm text-slate-900">${escH(lead.name)}</span>`;
          html += `<span class="text-xs px-2 py-0.5 rounded-full font-medium ${color}">${escH(lead.status)}</span></div>`;
          html += `<span class="text-xs text-slate-400">${escH(date)}</span></div>`;
          html += `<p class="text-xs text-slate-600 mb-2">${escH(lead.project_description)}</p>`;
          html += '<div class="flex items-center gap-3 text-xs">';
          html += `<a href="mailto:${escH(lead.email)}" class="text-blue-600 hover:underline">${escH(lead.email)}</a>`;
          if (lead.phone) html += `<a href="tel:${escH(lead.phone)}" class="text-blue-600 hover:underline">${escH(lead.phone)}</a>`;
          html += `<select data-lead-id="${escH(lead.id)}" class="lead-status-sel ml-auto border border-slate-200 rounded px-2 py-0.5 text-xs">`;
          ['new', 'contacted', 'won', 'lost'].forEach((s) => {
            html += `<option value="${s}"${lead.status === s ? ' selected' : ''}>${s.charAt(0).toUpperCase() + s.slice(1)}</option>`;
          });
          html += '</select></div></div>';
        });
        list.innerHTML = html;
        list.querySelectorAll('.lead-status-sel').forEach((sel) => {
          sel.addEventListener('change', () => {
            authFetch(`${AUTH_API}/api/leads/${encodeURIComponent(placeId)}/${sel.getAttribute('data-lead-id')}`, {
              method: 'PATCH', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: sel.value })
            });
          });
        });
      })
      .catch(() => { list.innerHTML = '<p class="text-sm text-slate-400">Unable to load leads.</p>'; });
  };

  // ── Portfolio Tab ──
  let portfolioProjects = [];

  const loadOwnerPortfolio = (placeId) => {
    authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/portfolio`)
      .then(r => r.json())
      .then(d => {
        portfolioProjects = (d.ok && d.projects) ? d.projects : [];
        renderPortfolio(placeId);
      });

    const form = document.getElementById('owner-portfolio-form');
    if (form) {
      form.onsubmit = (e) => {
        e.preventDefault();
        const title = document.getElementById('portfolio-title').value.trim();
        const desc = document.getElementById('portfolio-desc').value.trim();
        const msg = document.getElementById('portfolio-add-msg');
        if (!title) return;
        authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/portfolio`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: title, description: desc || null })
        })
          .then(r => r.json())
          .then(d => {
            if (d.ok) {
              portfolioProjects.push({ id: d.project_id, title: title, description: desc || null, before_photo_url: null, after_photo_url: null });
              renderPortfolio(placeId);
              form.reset();
              if (msg) { msg.classList.remove('hidden'); msg.className = 'text-xs text-green-700'; msg.textContent = 'Created!'; setTimeout(() => { msg.classList.add('hidden'); }, 3000); }
            } else {
              if (msg) { msg.classList.remove('hidden'); msg.className = 'text-xs text-red-600'; msg.textContent = d.error || 'Failed.'; }
            }
          });
      };
    }
  };

  const renderPortfolio = (placeId) => {
    const list = document.getElementById('owner-portfolio-list');
    if (!list) return;
    if (portfolioProjects.length === 0) {
      list.innerHTML = ownerEmptyState('Show customers what you can do', 'Add your first project below. Before-and-after photos make your work easy to understand.');
      return;
    }
    let html = '';
    portfolioProjects.forEach((p) => {
      html += '<div class="bg-white border border-slate-200 rounded-xl overflow-hidden">';
      html += '<div class="p-3 border-b border-slate-100"><div class="flex items-center justify-between">';
      html += `<h4 class="font-semibold text-sm text-slate-900">${escH(p.title)}</h4>`;
      html += `<button type="button" data-pid="${escH(p.id)}" class="port-del text-xs text-red-500 hover:text-red-700">&times; Delete</button>`;
      html += '</div>';
      if (p.description) html += `<p class="text-xs text-slate-500 mt-0.5">${escH(p.description)}</p>`;
      html += '</div><div class="grid grid-cols-2 gap-px bg-slate-200">';
      ['before', 'after'].forEach((type) => {
        const url = type === 'before' ? p.before_photo_url : p.after_photo_url;
        html += `<div class="bg-white p-2"><p class="text-[10px] font-semibold text-slate-400 uppercase mb-1">${type}</p>`;
        if (url) {
          html += `<img src="${escH(url)}" class="w-full h-28 object-cover rounded">`;
        } else {
          html += '<label class="flex items-center justify-center h-28 bg-slate-50 rounded border-2 border-dashed border-slate-200 cursor-pointer hover:border-green-300 transition-colors">';
          html += '<span class="text-xs text-slate-400">+ Upload</span>';
          html += `<input type="file" accept="image/jpeg,image/png,image/webp" class="hidden port-photo" data-pid="${escH(p.id)}" data-type="${type}"></label>`;
        }
        html += '</div>';
      });
      html += '</div></div>';
    });
    list.innerHTML = html;

    list.querySelectorAll('.port-photo').forEach((input) => {
      input.addEventListener('change', () => {
        const file = input.files[0]; if (!file) return;
        const pid = input.getAttribute('data-pid'), pt = input.getAttribute('data-type');
        const ct = file.type;
        if (!['image/jpeg','image/png','image/webp'].includes(ct)) return;
        const label = input.closest('label');
        const parentCell = label ? label.parentElement : list;
        // Hide the upload label, show thumbnail in its place
        if (label) label.style.display = 'none';
        const progress = createProgressBar(parentCell, file);
        authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/portfolio/${pid}/photo`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content_type: ct, photo_type: pt })
        }).then(r => r.json()).then(d => {
          if (!d.ok) { progress.error(d.error || 'Upload failed'); if (label) label.style.display = ''; return; }
          return uploadWithProgress(d.upload_url, file, ct, (pct) => { progress.update(pct); }).then(() => {
            portfolioProjects.forEach((p) => { if (p.id === pid) p[pt === 'before' ? 'before_photo_url' : 'after_photo_url'] = d.cdn_url; });
            progress.done(d.cdn_url);
            setTimeout(() => { renderPortfolio(placeId); }, 1300);
          });
        }).catch(() => { progress.error('Network error'); if (label) label.style.display = ''; });
      });
    });

    list.querySelectorAll('.port-del').forEach((btn) => {
      btn.addEventListener('click', () => {
        const pid = btn.getAttribute('data-pid');
        showConfirm('Delete this project and its photos?', { confirmText: 'Delete' }).then(ok => {
          if (!ok) return;
          const card = btn.closest('.rounded-xl');
          if (card) { card.style.transition = 'opacity 0.3s, transform 0.3s'; card.style.opacity = '0'; card.style.transform = 'scale(0.95)'; }
          portfolioProjects = portfolioProjects.filter(p => p.id !== pid);
          setTimeout(() => { renderPortfolio(placeId); }, 300);
          authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/portfolio/${pid}`, { method: 'DELETE' });
        });
      });
    });
  };

  // ── Jobs Pipeline Tab ──
  const JOB_STATUSES = ['lead', 'booked', 'in_progress', 'complete', 'paid'];
  const JOB_LABELS = { lead: 'Lead', booked: 'Booked', in_progress: 'In Progress', complete: 'Complete', paid: 'Paid' };
  const JOB_COLORS = { lead: 'badge-ghost', booked: 'badge-info', in_progress: 'badge-warning', complete: 'badge-success', paid: 'badge-neutral' };
  let allJobs = [];

  const loadJobsPipeline = (placeId) => {
    const container = document.getElementById('jobs-pipeline');
    if (!container) return;

    // Toggle form
    const addBtn = document.getElementById('job-add-btn');
    const addWrap = document.getElementById('job-add-form-wrap');
    const cancelBtn = document.getElementById('job-add-cancel');
    if (addBtn) addBtn.onclick = () => { addWrap.style.display = ''; addBtn.style.display = 'none'; };
    if (cancelBtn) cancelBtn.onclick = () => { addWrap.style.display = 'none'; addBtn.style.display = ''; };

    // Create form
    const form = document.getElementById('job-add-form');
    if (form) {
      form.onsubmit = (e) => {
        e.preventDefault();
        const amountVal = document.getElementById('job-amount').value;
        authFetch(`${AUTH_API}/auth/tools/jobs/${encodeURIComponent(placeId)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_name: document.getElementById('job-customer-name').value.trim(),
            title: document.getElementById('job-title').value.trim(),
            customer_email: document.getElementById('job-customer-email').value.trim() || null,
            customer_phone: document.getElementById('job-customer-phone').value.trim() || null,
            description: document.getElementById('job-description').value.trim() || null,
            amount_cents: amountVal ? Math.round(parseFloat(amountVal) * 100) : null,
          })
        }).then(r => r.json()).then(d => {
          if (d.ok) { form.reset(); addWrap.style.display = 'none'; addBtn.style.display = ''; fetchJobs(); }
        });
      };
    }

    const fetchJobs = () => {
      authFetch(`${AUTH_API}/auth/tools/jobs/${encodeURIComponent(placeId)}`)
        .then(r => r.json())
        .then(d => {
          allJobs = (d.ok && d.jobs) ? d.jobs : [];
          renderJobs(placeId);
        })
        .catch(() => { container.innerHTML = '<p class="text-sm text-slate-400">Unable to load jobs.</p>'; });
    };
    fetchJobs();
  };

  let jobFilter = 'all'; // all, or a specific status

  const renderJobs = (placeId) => {
    const container = document.getElementById('jobs-pipeline');
    if (!container) return;

    // Summary stats
    const counts = {};
    const revenue = {};
    JOB_STATUSES.forEach(s => { counts[s] = 0; revenue[s] = 0; });
    allJobs.forEach(j => { counts[j.status] = (counts[j.status] || 0) + 1; revenue[j.status] = (revenue[j.status] || 0) + (j.amount_cents || 0); });
    const totalRevenue = allJobs.reduce((s, j) => s + (j.amount_cents || 0), 0);
    const activeJobs = allJobs.filter(j => j.status !== 'paid').length;

    if (allJobs.length === 0) {
      container.innerHTML = ownerEmptyState('No jobs in your pipeline yet', 'Create your first job with the button above, or convert an incoming lead or booking when one arrives.');
      return;
    }

    let html = '';

    // Dashboard cards
    html += `<div class="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">`;
    html += `<div class="card bg-base-100 border border-base-300 p-3"><p class="text-2xl font-bold">${activeJobs}</p><p class="text-xs opacity-50">Active Jobs</p></div>`;
    html += `<div class="card bg-base-100 border border-base-300 p-3"><p class="text-2xl font-bold">$${(totalRevenue / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}</p><p class="text-xs opacity-50">Total Pipeline</p></div>`;
    const completedRev = revenue['complete'] || 0;
    const paidRev = revenue['paid'] || 0;
    html += `<div class="card bg-base-100 border border-base-300 p-3"><p class="text-2xl font-bold text-success">$${((paidRev) / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}</p><p class="text-xs opacity-50">Collected</p></div>`;
    html += `<div class="card bg-base-100 border border-base-300 p-3"><p class="text-2xl font-bold text-warning">$${((completedRev) / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}</p><p class="text-xs opacity-50">Awaiting Payment</p></div>`;
    html += `</div>`;

    // Pipeline filter tabs
    html += `<div class="flex flex-wrap gap-1 mb-3">`;
    html += `<button class="btn btn-xs ${jobFilter === 'all' ? 'btn-primary' : 'btn-ghost'} job-filter" data-filter="all">All (${allJobs.length})</button>`;
    JOB_STATUSES.forEach(s => {
      if (counts[s] > 0) {
        const revStr = revenue[s] > 0 ? ` · $${(revenue[s] / 100).toLocaleString()}` : '';
        html += `<button class="btn btn-xs ${jobFilter === s ? 'btn-primary' : 'btn-ghost'} job-filter" data-filter="${s}"><span class="badge ${JOB_COLORS[s]} badge-xs mr-1"></span>${JOB_LABELS[s]} (${counts[s]}${revStr})</button>`;
      }
    });
    html += `</div>`;

    // Filtered jobs
    const filtered = jobFilter === 'all' ? allJobs : allJobs.filter(j => j.status === jobFilter);
    if (filtered.length === 0) {
      html += `<p class="text-sm text-slate-400 py-4 text-center">No jobs in this status.</p>`;
    }

    filtered.forEach(job => {
        const status = job.status;
        const amount = job.amount_cents ? `$${(job.amount_cents / 100).toFixed(2)}` : '';
        const date = job.scheduled_date || '';
        html += `<div class="card bg-base-100 border border-base-300 p-3 mb-1.5" data-job-id="${escH(job.id)}">`;
        html += `<div class="flex items-start justify-between gap-2">`;
        html += `<div class="flex-1 min-w-0">`;
        html += `<div class="flex items-center gap-2"><p class="text-sm font-semibold truncate">${escH(job.title)}</p><span class="badge ${JOB_COLORS[status]} badge-xs">${JOB_LABELS[status]}</span></div>`;
        html += `<p class="text-xs opacity-60">${escH(job.customer_name)}${date ? ` · ${escH(date)}` : ''}${amount ? ` · ${amount}` : ''}</p>`;
        // Contact links
        const contacts = [];
        if (job.customer_email) contacts.push(`<a href="mailto:${escH(job.customer_email)}" class="link link-primary text-xs">Email</a>`);
        if (job.customer_phone) contacts.push(`<a href="tel:${escH(job.customer_phone)}" class="link link-primary text-xs">Call</a>`);
        if (contacts.length) html += `<p class="mt-0.5">${contacts.join(' · ')}</p>`;
        if (job.description) html += `<p class="text-xs opacity-50 mt-1 line-clamp-2">${escH(job.description)}</p>`;
        html += `</div>`;
        html += `<div class="flex flex-col items-end gap-1 flex-shrink-0">`;
        // Status advance button
        const idx = JOB_STATUSES.indexOf(status);
        if (idx < JOB_STATUSES.length - 1) {
          const nextStatus = JOB_STATUSES[idx + 1];
          html += `<button class="btn btn-xs btn-primary job-advance" data-jid="${escH(job.id)}" data-next="${nextStatus}">&rarr; ${JOB_LABELS[nextStatus]}</button>`;
        }
        // Review request (when complete)
        if (status === 'complete' && !job.review_requested && job.customer_email) {
          html += `<button class="btn btn-xs btn-outline btn-success job-review-req" data-jid="${escH(job.id)}" data-name="${escH(job.customer_name)}" data-email="${escH(job.customer_email || '')}" data-phone="${escH(job.customer_phone || '')}">Ask for Review</button>`;
        }
        if (job.review_requested) html += `<span class="badge badge-success badge-xs">Review sent</span>`;
        // Edit + delete
        html += `<div class="flex items-center gap-1">`;
        html += `<button class="btn btn-xs btn-ghost job-edit" data-jid="${escH(job.id)}">Edit</button>`;
        html += `<button class="btn btn-xs btn-ghost text-error job-del" data-jid="${escH(job.id)}">&times;</button>`;
        html += `</div>`;
        html += `</div></div>`;
        // Inline edit form (hidden)
        html += `<div class="job-edit-form mt-2" data-jid="${escH(job.id)}" style="display:none">`;
        html += `<div class="grid grid-cols-2 gap-2 mb-2">`;
        html += `<input type="text" class="input input-xs w-full je-title" value="${escH(job.title)}" placeholder="Title">`;
        html += `<input type="text" class="input input-xs w-full je-name" value="${escH(job.customer_name)}" placeholder="Customer">`;
        html += `<input type="email" class="input input-xs w-full je-email" value="${escH(job.customer_email || '')}" placeholder="Email">`;
        html += `<input type="tel" class="input input-xs w-full je-phone" value="${escH(job.customer_phone || '')}" placeholder="Phone">`;
        html += `<input type="date" class="input input-xs w-full je-date" value="${escH(job.scheduled_date || '')}">`;
        html += `<input type="number" class="input input-xs w-full je-amount" value="${job.amount_cents ? (job.amount_cents / 100).toFixed(2) : ''}" placeholder="Amount ($)" step="0.01" min="0">`;
        html += `</div>`;
        html += `<textarea class="textarea textarea-xs w-full mb-2 je-desc" rows="2" placeholder="Description">${escH(job.description || '')}</textarea>`;
        html += `<div class="flex gap-1">`;
        html += `<button class="btn btn-xs btn-success job-save" data-jid="${escH(job.id)}">Save</button>`;
        html += `<button class="btn btn-xs btn-ghost job-edit-cancel" data-jid="${escH(job.id)}">Cancel</button>`;
        html += `</div></div>`;
        html += `</div>`;
    });

    container.innerHTML = html;

    // Filter tab handlers
    container.querySelectorAll('.job-filter').forEach(btn => {
      btn.addEventListener('click', () => {
        jobFilter = btn.dataset.filter;
        renderJobs(placeId);
      });
    });

    // Advance handlers
    container.querySelectorAll('.job-advance').forEach(btn => {
      btn.addEventListener('click', () => {
        const jid = btn.dataset.jid;
        const next = btn.dataset.next;
        // Optimistic update
        allJobs.forEach(j => { if (j.id === jid) j.status = next; });
        renderJobs(placeId);
        authFetch(`${AUTH_API}/auth/tools/jobs/${encodeURIComponent(placeId)}/${jid}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: next })
        });
      });
    });

    // Review request handlers
    container.querySelectorAll('.job-review-req').forEach(btn => {
      btn.addEventListener('click', () => {
        const jid = btn.dataset.jid;
        const method = 'email'; // SMS disabled — requires Twilio
        btn.disabled = true; btn.textContent = 'Sending...';
        authFetch(`${AUTH_API}/auth/tools/review-requests/${encodeURIComponent(placeId)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            job_id: jid, customer_name: btn.dataset.name,
            customer_email: btn.dataset.email || null,
            customer_phone: btn.dataset.phone || null,
            method,
          })
        }).then(r => r.json()).then(d => {
          if (d.ok) {
            allJobs.forEach(j => { if (j.id === jid) j.review_requested = 1; });
            renderJobs(placeId);
          } else { btn.disabled = false; btn.textContent = 'Ask for Review'; }
        });
      });
    });

    // Edit toggle
    container.querySelectorAll('.job-edit').forEach(btn => {
      btn.addEventListener('click', () => {
        const form = container.querySelector(`.job-edit-form[data-jid="${btn.dataset.jid}"]`);
        if (form) form.style.display = form.style.display === 'none' ? '' : 'none';
      });
    });
    container.querySelectorAll('.job-edit-cancel').forEach(btn => {
      btn.addEventListener('click', () => {
        const form = container.querySelector(`.job-edit-form[data-jid="${btn.dataset.jid}"]`);
        if (form) form.style.display = 'none';
      });
    });

    // Save edit
    container.querySelectorAll('.job-save').forEach(btn => {
      btn.addEventListener('click', () => {
        const jid = btn.dataset.jid;
        const form = container.querySelector(`.job-edit-form[data-jid="${jid}"]`);
        if (!form) return;
        const amountVal = form.querySelector('.je-amount').value;
        const data = {
          title: form.querySelector('.je-title').value.trim(),
          customer_name: form.querySelector('.je-name').value.trim(),
          customer_email: form.querySelector('.je-email').value.trim() || null,
          customer_phone: form.querySelector('.je-phone').value.trim() || null,
          scheduled_date: form.querySelector('.je-date').value || null,
          description: form.querySelector('.je-desc').value.trim() || null,
          amount_cents: amountVal ? Math.round(parseFloat(amountVal) * 100) : null,
        };
        btn.disabled = true; btn.textContent = 'Saving...';
        authFetch(`${AUTH_API}/auth/tools/jobs/${encodeURIComponent(placeId)}/${jid}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        }).then(r => r.json()).then(d => {
          if (d.ok) {
            // Update local data
            allJobs.forEach(j => { if (j.id === jid) Object.assign(j, data); });
            renderJobs(placeId);
          }
          btn.disabled = false; btn.textContent = 'Save';
        });
      });
    });

    // Delete job
    container.querySelectorAll('.job-del').forEach(btn => {
      btn.addEventListener('click', () => {
        showConfirm('Delete this job?', { confirmText: 'Delete' }).then(ok => {
          if (!ok) return;
          const jid = btn.dataset.jid;
          allJobs = allJobs.filter(j => j.id !== jid);
          renderJobs(placeId);
          authFetch(`${AUTH_API}/auth/tools/jobs/${encodeURIComponent(placeId)}/${jid}`, { method: 'DELETE' });
        });
      });
    });
  };

  // ── Bookings Tab ──
  const loadBookings = (placeId) => {
    const list = document.getElementById('bookings-list');
    if (!list) return;

    authFetch(`${AUTH_API}/auth/tools/bookings/${encodeURIComponent(placeId)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.ok || !d.bookings || d.bookings.length === 0) {
          list.innerHTML = ownerEmptyState(
            'Bookings will appear here',
            'When a customer requests an appointment from your business profile, you can review and manage it here.',
            `<a class="btn btn-primary btn-sm" href="${escH(ownerProfileUrl)}" target="_blank" rel="noopener">View public profile</a>`,
          );
          return;
        }

        const statusColors = { pending: 'badge-warning', confirmed: 'badge-info', completed: 'badge-success', cancelled: 'badge-ghost' };
        let html = '';
        d.bookings.forEach(b => {
          const color = statusColors[b.status] || 'badge-ghost';
          const date = b.created_at ? new Date(b.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
          html += `<div class="card bg-base-100 border border-base-300 p-3">`;
          html += `<div class="flex items-center justify-between mb-1">`;
          html += `<div class="flex items-center gap-2"><span class="font-semibold text-sm">${escH(b.customer_name)}</span><span class="badge ${color} badge-xs">${escH(b.status)}</span></div>`;
          html += `<span class="text-xs opacity-50">${escH(date)}</span></div>`;
          if (b.service_type) html += `<p class="text-xs opacity-70 mb-1">${escH(b.service_type)}</p>`;
          if (b.preferred_date) html += `<p class="text-xs opacity-60">${escH(b.preferred_date)}${b.preferred_time ? ` · ${escH(b.preferred_time)}` : ''}</p>`;
          if (b.notes) html += `<p class="text-xs opacity-50 mt-1">${escH(b.notes)}</p>`;
          html += `<div class="flex items-center gap-2 mt-2">`;
          if (b.customer_email) html += `<a href="mailto:${escH(b.customer_email)}" class="btn btn-xs btn-ghost">Email</a>`;
          if (b.customer_phone) html += `<a href="tel:${escH(b.customer_phone)}" class="btn btn-xs btn-ghost">Call</a>`;
          if (b.status === 'pending') {
            html += `<button class="btn btn-xs btn-success booking-convert" data-bid="${escH(b.id)}">Convert to Job</button>`;
            html += `<button class="btn btn-xs btn-ghost booking-status" data-bid="${escH(b.id)}" data-status="cancelled">Decline</button>`;
          }
          html += `</div></div>`;
        });
        list.innerHTML = html;

        // Convert to job
        list.querySelectorAll('.booking-convert').forEach(btn => {
          btn.addEventListener('click', () => {
            btn.disabled = true; btn.textContent = 'Converting...';
            authFetch(`${AUTH_API}/auth/tools/jobs/${encodeURIComponent(placeId)}/from-booking/${btn.dataset.bid}`, { method: 'POST' })
              .then(r => r.json())
              .then(d => {
                if (d.ok) { loadBookings(placeId); loadJobsPipeline(placeId); }
                else { btn.disabled = false; btn.textContent = 'Convert to Job'; }
              });
          });
        });

        // Status update
        list.querySelectorAll('.booking-status').forEach(btn => {
          btn.addEventListener('click', () => {
            authFetch(`${AUTH_API}/auth/tools/bookings/${encodeURIComponent(placeId)}/${btn.dataset.bid}`, {
              method: 'PATCH', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: btn.dataset.status })
            }).then(() => loadBookings(placeId));
          });
        });
      })
      .catch(() => { list.innerHTML = '<p class="text-sm text-slate-400">Unable to load bookings.</p>'; });
  };

  // ── Estimates Tab ──
  let estLineCount = 0;

  const loadEstimates = (placeId) => {
    const list = document.getElementById('estimates-list');
    if (!list) return;

    // Toggle form
    const addBtn = document.getElementById('estimate-add-btn');
    const addWrap = document.getElementById('estimate-add-form-wrap');
    const cancelBtn = document.getElementById('estimate-add-cancel');
    if (addBtn) addBtn.onclick = () => { addWrap.style.display = ''; addBtn.style.display = 'none'; addEstLine(); };
    if (cancelBtn) cancelBtn.onclick = () => { addWrap.style.display = 'none'; addBtn.style.display = ''; };

    // Add line item button
    const addLineBtn = document.getElementById('est-add-line');
    if (addLineBtn) addLineBtn.onclick = () => addEstLine();

    // Create form
    const form = document.getElementById('estimate-add-form');
    if (form) {
      form.onsubmit = (e) => {
        e.preventDefault();
        const lines = [];
        document.querySelectorAll('.est-line-row').forEach(row => {
          const desc = row.querySelector('.est-line-desc').value.trim();
          const qty = parseInt(row.querySelector('.est-line-qty').value) || 1;
          const price = Math.round(parseFloat(row.querySelector('.est-line-price').value || '0') * 100);
          if (desc) lines.push({ description: desc, quantity: qty, unit_price_cents: price });
        });

        authFetch(`${AUTH_API}/auth/tools/estimates/${encodeURIComponent(placeId)}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_name: document.getElementById('est-customer-name').value.trim(),
            customer_email: document.getElementById('est-customer-email').value.trim() || null,
            customer_phone: document.getElementById('est-customer-phone').value.trim() || null,
            title: document.getElementById('est-title').value.trim(),
            notes: document.getElementById('est-notes').value.trim() || null,
            lines,
          })
        }).then(r => r.json()).then(d => {
          if (d.ok) { form.reset(); document.getElementById('est-lines').innerHTML = ''; estLineCount = 0; addWrap.style.display = 'none'; addBtn.style.display = ''; fetchEstimates(); }
        });
      };
    }

    const fetchEstimates = () => {
      authFetch(`${AUTH_API}/auth/tools/estimates/${encodeURIComponent(placeId)}`)
        .then(r => r.json())
        .then(d => {
          if (!d.ok || !d.estimates || d.estimates.length === 0) {
            list.innerHTML = ownerEmptyState('Create your first estimate', 'Build a professional estimate for a customer using the “New Estimate” button above.');
            return;
          }
          const statusColors = { draft: 'badge-ghost', sent: 'badge-info', approved: 'badge-success', rejected: 'badge-error', voided: 'badge-ghost' };
          let html = '';
          d.estimates.forEach(est => {
            const total = `$${(est.total_cents / 100).toFixed(2)}`;
            const color = statusColors[est.status] || 'badge-ghost';
            const date = est.created_at ? new Date(est.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
            html += `<div class="card bg-base-100 border border-base-300 p-3">`;
            html += `<div class="flex items-center justify-between">`;
            html += `<div><p class="text-sm font-semibold">${escH(est.title)}</p>`;
            html += `<p class="text-xs opacity-60">${escH(est.customer_name)} · ${total}</p></div>`;
            html += `<div class="flex items-center gap-2"><span class="badge ${color} badge-xs">${escH(est.status)}</span><span class="text-xs opacity-50">${escH(date)}</span></div>`;
            html += `</div>`;
            html += `<div class="flex items-center gap-1 mt-2">`;
            if (est.status === 'draft') {
              html += `<button class="btn btn-xs btn-primary est-send" data-eid="${escH(est.id)}">Send to Customer</button>`;
            }
            if (est.share_token) {
              html += `<button class="btn btn-xs btn-ghost est-copy" data-token="${escH(est.share_token)}">Copy Link</button>`;
            }
            if (est.status === 'approved') {
              html += `<button class="btn btn-xs btn-success est-to-job" data-eid="${escH(est.id)}" data-name="${escH(est.customer_name)}" data-email="${escH(est.customer_email || '')}" data-title="${escH(est.title)}" data-amount="${est.total_cents}">Create Job</button>`;
            }
            html += `<button class="btn btn-xs btn-ghost est-del" data-eid="${escH(est.id)}">Delete</button>`;
            html += `</div></div>`;
          });
          list.innerHTML = html;

          // Send estimate
          list.querySelectorAll('.est-send').forEach(btn => {
            btn.addEventListener('click', () => {
              btn.disabled = true; btn.textContent = 'Sending...';
              authFetch(`${AUTH_API}/auth/tools/estimates/${encodeURIComponent(placeId)}/${btn.dataset.eid}`, {
                method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: 'sent' })
              }).then(r => r.json()).then(d => { if (d.ok) fetchEstimates(); });
            });
          });

          // Copy link
          list.querySelectorAll('.est-copy').forEach(btn => {
            btn.addEventListener('click', () => {
              const url = `${location.origin}/estimate/${btn.dataset.token}`;
              navigator.clipboard.writeText(url).then(() => { btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy Link'; }, 2000); });
            });
          });

          // Create job from approved estimate
          list.querySelectorAll('.est-to-job').forEach(btn => {
            btn.addEventListener('click', () => {
              btn.disabled = true; btn.textContent = 'Creating...';
              authFetch(`${AUTH_API}/auth/tools/jobs/${encodeURIComponent(placeId)}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  customer_name: btn.dataset.name,
                  customer_email: btn.dataset.email || null,
                  title: btn.dataset.title,
                  amount_cents: parseInt(btn.dataset.amount) || null,
                  source: 'estimate', source_id: btn.dataset.eid,
                })
              }).then(r => r.json()).then(d => { if (d.ok) { fetchEstimates(); loadJobsPipeline(placeId); } });
            });
          });

          // Delete estimate
          list.querySelectorAll('.est-del').forEach(btn => {
            btn.addEventListener('click', () => {
              showConfirm('Delete this estimate?', { confirmText: 'Delete' }).then(ok => {
                if (!ok) return;
                authFetch(`${AUTH_API}/auth/tools/estimates/${encodeURIComponent(placeId)}/${btn.dataset.eid}`, { method: 'DELETE' })
                  .then(r => r.json()).then(d => { if (d.ok) fetchEstimates(); });
              });
            });
          });
        })
        .catch(() => { list.innerHTML = '<p class="text-sm text-slate-400">Unable to load estimates.</p>'; });
    };
    fetchEstimates();
  };

  const addEstLine = () => {
    const linesEl = document.getElementById('est-lines');
    if (!linesEl) return;
    estLineCount++;
    const row = document.createElement('div');
    row.className = 'est-line-row flex items-center gap-2';
    row.innerHTML = `
      <input type="text" placeholder="Description" class="input input-xs w-full est-line-desc" required>
      <input type="number" placeholder="Qty" class="input input-xs w-16 est-line-qty" value="1" min="1">
      <input type="number" placeholder="Price" class="input input-xs w-24 est-line-price" step="0.01" min="0">
      <button type="button" class="btn btn-ghost btn-xs est-line-remove">&times;</button>
    `;
    linesEl.appendChild(row);
    row.querySelector('.est-line-remove').addEventListener('click', () => { row.remove(); updateEstTotal(); });
    row.querySelectorAll('input').forEach(inp => inp.addEventListener('input', updateEstTotal));
  };

  const updateEstTotal = () => {
    let total = 0;
    document.querySelectorAll('.est-line-row').forEach(row => {
      const qty = parseInt(row.querySelector('.est-line-qty').value) || 1;
      const price = parseFloat(row.querySelector('.est-line-price').value) || 0;
      total += qty * price;
    });
    const display = document.getElementById('est-total-display');
    if (display) display.textContent = `Total: $${total.toFixed(2)}`;
  };

  // ── Reviews Tab (ProSource community reviews + review request) ──

  const loadCommunityReviews = (placeId) => {
    const list = document.getElementById('owner-community-reviews');
    if (!list) return;

    authFetch(`${AUTH_API}/auth/reviews?place_id=${encodeURIComponent(placeId)}`)
      .then(r => r.json())
      .then(d => {
        if (!d.ok || !d.reviews || d.reviews.length === 0) {
          list.innerHTML = ownerEmptyState('Your reviews will appear here', 'Request a review from a recent customer to start building trust on your profile.');
          return;
        }

        // Also fetch existing owner responses
        authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/responses`)
          .then(r => r.json())
          .then(respData => {
            const respMap = {};
            if (respData.ok && respData.responses) {
              respData.responses.forEach(r => { respMap[r.review_id] = r.response_text; });
            }

            let html = '';
            d.reviews.forEach(rev => {
              const existing = respMap[rev.id] || '';
              const date = rev.created_at ? new Date(rev.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
              let starHtml = '';
              for (let i = 1; i <= 5; i++) starHtml += `<svg class="w-3.5 h-3.5 ${i <= rev.rating ? 'text-amber-400' : 'text-slate-200'}" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.025 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z"/></svg>`;

              html += `<div class="card bg-base-100 border border-base-300 p-3">`;
              html += `<div class="flex items-center gap-2 mb-1">`;
              html += `<span class="font-semibold text-sm">${escH(rev.user_name || 'Anonymous')}</span>`;
              html += `<div class="flex">${starHtml}</div>`;
              html += `<span class="text-xs opacity-50">${escH(date)}</span>`;
              html += `</div>`;
              html += `<p class="text-sm opacity-80 mb-2">${escH(rev.review_text)}</p>`;

              if (existing) {
                html += `<div class="bg-success/10 rounded-lg py-2 px-3 mb-2">`;
                html += `<p class="text-xs font-semibold mb-0.5">Your response</p>`;
                html += `<p class="text-xs opacity-80">${escH(existing)}</p>`;
                html += `</div>`;
                html += `<button type="button" data-rid="${escH(rev.id)}" class="btn btn-ghost btn-xs cr-edit-btn">Edit response</button>`;
              } else {
                html += `<button type="button" data-rid="${escH(rev.id)}" class="btn btn-ghost btn-xs cr-respond-btn">Respond</button>`;
              }

              html += `<div class="cr-form mt-2" data-rid="${escH(rev.id)}" style="display:none">`;
              html += `<textarea class="textarea textarea-sm w-full" rows="2" maxlength="2000" placeholder="Write your response...">${escH(existing)}</textarea>`;
              html += `<div class="flex items-center gap-2 mt-1">`;
              html += `<button type="button" class="btn btn-success btn-xs cr-save" data-rid="${escH(rev.id)}">${existing ? 'Update' : 'Reply'}</button>`;
              html += `<button type="button" class="btn btn-ghost btn-xs cr-cancel" data-rid="${escH(rev.id)}">Cancel</button>`;
              html += `<span class="cr-msg text-xs hidden" data-rid="${escH(rev.id)}"></span>`;
              html += `</div></div>`;
              html += `</div>`;
            });

            list.innerHTML = html;

            // Toggle response form
            list.querySelectorAll('.cr-respond-btn, .cr-edit-btn').forEach(btn => {
              btn.addEventListener('click', () => {
                const form = list.querySelector(`.cr-form[data-rid="${btn.dataset.rid}"]`);
                if (form) { form.style.display = ''; btn.style.display = 'none'; form.querySelector('textarea').focus(); }
              });
            });

            // Cancel
            list.querySelectorAll('.cr-cancel').forEach(btn => {
              btn.addEventListener('click', () => {
                const rid = btn.dataset.rid;
                const form = list.querySelector(`.cr-form[data-rid="${rid}"]`);
                if (form) form.style.display = 'none';
                const toggle = list.querySelector(`.cr-respond-btn[data-rid="${rid}"], .cr-edit-btn[data-rid="${rid}"]`);
                if (toggle) toggle.style.display = '';
              });
            });

            // Save response
            list.querySelectorAll('.cr-save').forEach(btn => {
              btn.addEventListener('click', () => {
                const rid = btn.dataset.rid;
                const form = list.querySelector(`.cr-form[data-rid="${rid}"]`);
                const textarea = form.querySelector('textarea');
                const msg = list.querySelector(`.cr-msg[data-rid="${rid}"]`);
                const text = textarea.value.trim();
                if (text.length < 5) { msg.className = 'cr-msg text-xs text-error'; msg.textContent = 'Min 5 characters'; msg.classList.remove('hidden'); return; }

                btn.disabled = true; btn.textContent = 'Saving...';
                authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/responses`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ review_id: rid, response_text: text })
                }).then(r => r.json()).then(d => {
                  if (d.ok) {
                    loadCommunityReviews(placeId); // re-render
                  } else {
                    msg.className = 'cr-msg text-xs text-error'; msg.textContent = d.error || 'Failed';
                    msg.classList.remove('hidden');
                    btn.disabled = false; btn.textContent = 'Retry';
                  }
                });
              });
            });
          });
      })
      .catch(() => {
        list.innerHTML = '<p class="text-sm text-slate-400">Unable to load reviews.</p>';
      });
  };

  const initReviewRequestForm = (placeId) => {
    const form = document.getElementById('review-request-form');
    if (!form) return;

    form.onsubmit = (e) => {
      e.preventDefault();
      const msg = document.getElementById('rr-msg');
      const name = document.getElementById('rr-name').value.trim();
      const email = document.getElementById('rr-email').value.trim();
      if (!name || !email) return;

      const btn = form.querySelector('button[type="submit"]');
      btn.disabled = true; btn.textContent = 'Sending...';
      if (msg) msg.classList.add('hidden');

      authFetch(`${AUTH_API}/auth/tools/review-requests/${encodeURIComponent(placeId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_name: name, customer_email: email, method: 'email' })
      }).then(r => r.json()).then(d => {
        btn.disabled = false; btn.textContent = 'Send Review Request';
        if (msg) {
          msg.classList.remove('hidden');
          if (d.ok) {
            msg.className = 'text-xs text-success'; msg.textContent = 'Review request sent!';
            form.reset();
            refreshOwnerActivation(placeId, {}, ownerPhotos);
          } else {
            msg.className = 'text-xs text-error'; msg.textContent = d.error || 'Failed to send';
          }
          setTimeout(() => msg.classList.add('hidden'), 3000);
        }
      }).catch(() => {
        btn.disabled = false; btn.textContent = 'Send Review Request';
        if (msg) { msg.classList.remove('hidden'); msg.className = 'text-xs text-error'; msg.textContent = 'Network error'; }
      });
    };
  };

  // ── Settings Tab ──
  const loadOwnerSettings = (placeId) => {
    authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/settings`)
      .then(r => r.json())
      .then(d => {
        if (!d.ok) return;
        const s = d.settings || {};
        const providerSel = document.getElementById('setting-booking-provider');
        const bookingUrl = document.getElementById('setting-booking-url');
        if (providerSel && s.booking_provider) { providerSel.value = s.booking_provider; if (s.booking_provider) bookingUrl.style.display = ''; }
        if (bookingUrl && s.booking_url) bookingUrl.value = s.booking_url;
        if (providerSel) providerSel.onchange = () => { bookingUrl.style.display = providerSel.value ? '' : 'none'; };

        if (s.badges) { try { const badges = JSON.parse(s.badges); document.querySelectorAll('.badge-checkbox').forEach((cb) => { cb.checked = badges.indexOf(cb.value) !== -1; }); } catch(e) {} }

        const smsCb = document.getElementById('setting-sms-enabled');
        if (smsCb) smsCb.checked = s.sms_enabled === 'on';

        const finCb = document.getElementById('setting-financing-enabled');
        const finUrl = document.getElementById('setting-financing-url');
        if (finCb) { finCb.checked = s.financing_enabled === 'on'; if (finCb.checked && finUrl) finUrl.style.display = ''; finCb.onchange = () => { if (finUrl) finUrl.style.display = finCb.checked ? '' : 'none'; }; }
        if (finUrl && s.financing_url) finUrl.value = s.financing_url;

        const revNotif = document.getElementById('setting-review-notifications');
        const leadNotif = document.getElementById('setting-lead-notifications');
        if (revNotif) revNotif.checked = s.review_notifications !== 'off';
        if (leadNotif) leadNotif.checked = s.lead_notifications !== 'off';
      });

    const saveBtn = document.getElementById('owner-settings-save');
    if (saveBtn) {
      saveBtn.onclick = () => {
        const msg = document.getElementById('owner-settings-msg');
        saveBtn.disabled = true; saveBtn.textContent = 'Saving...';
        if (msg) msg.classList.add('hidden');
        const badges = [];
        document.querySelectorAll('.badge-checkbox:checked').forEach((cb) => { badges.push(cb.value); });
        const settings = {
          booking_provider: document.getElementById('setting-booking-provider').value || '',
          booking_url: document.getElementById('setting-booking-url').value.trim() || '',
          badges: JSON.stringify(badges),
          sms_enabled: (document.getElementById('setting-sms-enabled') || {}).checked ? 'on' : 'off',
          financing_enabled: document.getElementById('setting-financing-enabled').checked ? 'on' : 'off',
          financing_url: document.getElementById('setting-financing-url').value.trim() || '',
          review_notifications: document.getElementById('setting-review-notifications').checked ? 'on' : 'off',
          lead_notifications: document.getElementById('setting-lead-notifications').checked ? 'on' : 'off'
        };
        authFetch(`${AUTH_API}/auth/business/${encodeURIComponent(placeId)}/settings`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(settings)
        })
          .then(r => r.json())
          .then(d => {
            saveBtn.disabled = false; saveBtn.textContent = 'Save Settings';
            if (msg) { msg.classList.remove('hidden'); msg.className = d.ok ? 'text-sm text-green-700' : 'text-sm text-red-600'; msg.textContent = d.ok ? 'Settings saved!' : (d.error || 'Failed.'); setTimeout(() => { msg.classList.add('hidden'); }, 3000); }
            if (d.ok) refreshOwnerActivation(placeId, {}, ownerPhotos);
          })
          .catch(() => { saveBtn.disabled = false; saveBtn.textContent = 'Save Settings'; if (msg) { msg.classList.remove('hidden'); msg.className = 'text-sm text-red-600'; msg.textContent = 'Network error.'; } });
      };
    }
  };

  // ── Magic link form ──
  document.getElementById('magic-form').addEventListener('submit', (e) => {
    e.preventDefault();
    const email = document.getElementById('magic-email').value;
    const btn = document.getElementById('magic-btn');
    const msg = document.getElementById('magic-msg');
    btn.disabled = true; btn.textContent = 'Sending...';
    msg.classList.add('hidden');
    authFetch(`${AUTH_API}/auth/magic-link`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, redirect: getReturnPath() }), credentials: 'include'
    })
    .then(r => r.json())
    .then(d => {
      btn.disabled = false; btn.textContent = 'Send sign-in link';
      msg.classList.remove('hidden');
      msg.className = `text-sm mb-3 ${d.ok ? 'text-green-700' : 'text-red-600'}`;
      msg.textContent = d.ok ? (d.message || 'Check your email for a sign-in link.') : (d.error || 'Something went wrong.');
    })
    .catch(() => {
      btn.disabled = false; btn.textContent = 'Send sign-in link';
      msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-red-600';
      msg.textContent = 'Network error. Please try again.';
    });
  });

  // ── Edit name ──
  document.getElementById('edit-name-form').addEventListener('submit', (e) => {
    e.preventDefault();
    const name = document.getElementById('edit-name-input').value.trim();
    const msg = document.getElementById('edit-name-msg');
    authFetch(`${AUTH_API}/auth/me`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name }), credentials: 'include'
    })
    .then(r => r.json())
    .then(d => {
      msg.classList.remove('hidden');
      if (d.ok) {
        msg.className = 'text-sm mt-2 text-green-700'; msg.textContent = 'Name updated.';
        currentUser.name = name;
        document.getElementById('dash-name').textContent = name || 'No name set';
      } else {
        msg.className = 'text-sm mt-2 text-red-600'; msg.textContent = d.error || 'Failed to update.';
      }
    })
    .catch(() => { msg.classList.remove('hidden'); msg.className = 'text-sm mt-2 text-red-600'; msg.textContent = 'Network error.'; });
  });

  // ── Logout ──
  document.getElementById('logout-btn').addEventListener('click', () => {
    authFetch(`${AUTH_API}/auth/logout`, { method: 'POST', credentials: 'include' })
      .then(() => { localStorage.removeItem('ps_dev_token'); devToken = null; currentUser = null; show(loginView); })
      .catch(() => { localStorage.removeItem('ps_dev_token'); devToken = null; currentUser = null; show(loginView); });
  });

  // ── Add business request ──
  const addBizForm = document.getElementById('add-biz-form');
  if (addBizForm) {
    addBizForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const fd = new FormData(addBizForm);
      const msg = document.getElementById('add-biz-msg');
      const btn = addBizForm.querySelector('button[type="submit"]');
      btn.disabled = true;
      btn.textContent = 'Submitting...';
      authFetch('/api/business-requests', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: fd.get('business_name'),
          city: fd.get('city'),
          state: fd.get('state'),
          website: fd.get('website') || '',
          notes: fd.get('notes') || '',
          email: currentUser?.email || '',
        }),
      })
        .then(r => r.json())
        .then(d => {
          msg.classList.remove('hidden');
          if (d.ok) {
            msg.className = 'text-sm text-success';
            msg.textContent = d.message || 'Request submitted! We\'ll review it shortly.';
            addBizForm.reset();
          } else {
            msg.className = 'text-sm text-error';
            msg.textContent = d.error || 'Something went wrong.';
          }
        })
        .catch(() => {
          msg.classList.remove('hidden');
          msg.className = 'text-sm text-error';
          msg.textContent = 'Network error. Please try again.';
        })
        .finally(() => {
          btn.disabled = false;
          btn.textContent = 'Submit Request';
        });
    });
  }

  // ── Deactivate ──
  document.getElementById('deactivate-btn').addEventListener('click', () => {
    showConfirm('Are you sure you want to deactivate your account? This will permanently delete all your data including reviews, favorites, and business claims. This cannot be undone.', { confirmText: 'Deactivate Account' }).then(ok => {
      if (!ok) return;
      authFetch(`${AUTH_API}/auth/me`, { method: 'DELETE', credentials: 'include' })
        .then(r => r.json())
        .then(d => {
          if (d.ok) { currentUser = null; show(loginView); }
          else showAlert(`Failed to deactivate: ${d.error || 'Unknown error'}`, 'error');
        })
        .catch(() => { showAlert('Network error. Please try again.', 'error'); });
    });
  });

  // ── Claim verify modal ──
  const showClaimVerifyModal = (claimId, domain, contactEmail, phone) => {
    const modal = document.getElementById('acct-verify-modal');
    const desc = document.getElementById('acct-verify-desc');
    const emailInput = document.getElementById('acct-verify-email');
    const msg = document.getElementById('acct-verify-msg');
    const submitBtn = document.getElementById('acct-verify-btn');
    const hasEmail = !!(domain || contactEmail);
    const mode = hasEmail ? 'email' : 'phone';
    if (mode === 'email') {
      if (domain && contactEmail) {
        desc.textContent = `Enter an email address at @${domain} or the business contact email to verify you own this business.`;
        emailInput.placeholder = `you@${domain}`;
      } else if (domain) {
        desc.textContent = `Enter an email address at @${domain} to verify you own this business.`;
        emailInput.placeholder = `you@${domain}`;
      } else {
        desc.textContent = 'Enter the business contact email address to verify you own this business.';
        emailInput.placeholder = 'Business contact email';
      }
      emailInput.type = 'email';
      emailInput.style.display = '';
    } else {
      desc.textContent = `We'll send a verification code to the business phone number ${phone}.`;
      emailInput.type = 'hidden';
      emailInput.style.display = 'none';
    }
    emailInput.value = '';
    msg.classList.add('hidden'); submitBtn.disabled = false; submitBtn.textContent = 'Send verification email';
    if (mode === 'phone') submitBtn.textContent = 'Send verification code';
    modal.classList.add('modal-open');
    document.getElementById('acct-verify-cancel').onclick = () => { modal.classList.remove('modal-open'); };
    const backdrop = document.getElementById('acct-verify-backdrop');
    if (backdrop) backdrop.onclick = () => { modal.classList.remove('modal-open'); };
    document.getElementById('acct-verify-form').onsubmit = (e) => {
      e.preventDefault();
      submitBtn.disabled = true; submitBtn.textContent = 'Sending...';
      msg.classList.add('hidden');
      if (mode === 'email') {
        const email = emailInput.value.trim();
        if (!email) { submitBtn.disabled = false; return; }
        authFetch(`${AUTH_API}/auth/claims/${claimId}/send-verification`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email }), credentials: 'include'
        })
        .then(r => r.json())
        .then(d => {
          msg.classList.remove('hidden');
          if (d.ok) { msg.className = 'text-sm mb-3 text-green-700'; msg.textContent = d.message || 'Verification email sent!'; submitBtn.textContent = 'Email sent'; }
          else { msg.className = 'text-sm mb-3 text-red-600'; msg.textContent = d.error || 'Failed to send.'; submitBtn.disabled = false; submitBtn.textContent = 'Send verification email'; }
        })
        .catch(() => { msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-red-600'; msg.textContent = 'Network error.'; submitBtn.disabled = false; submitBtn.textContent = 'Send verification email'; });
      } else {
        authFetch(`${AUTH_API}/auth/claims/${claimId}/send-phone-code`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include'
        })
        .then(r => r.json())
        .then(d => {
          if (!d.ok) {
            msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-red-600';
            msg.textContent = d.error || 'Failed to send code.';
            submitBtn.disabled = false; submitBtn.textContent = 'Send verification code';
            return;
          }
          msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-green-700';
          msg.textContent = d.message || 'Code sent!';
          desc.textContent = 'Enter the 6-digit code:';
          emailInput.type = 'text'; emailInput.style.display = ''; emailInput.value = '';
          emailInput.placeholder = '000000'; emailInput.maxLength = 6;
          submitBtn.disabled = false; submitBtn.textContent = 'Verify code';
          document.getElementById('acct-verify-form').onsubmit = (ev) => {
            ev.preventDefault();
            const code = emailInput.value.trim();
            if (!code || code.length !== 6) return;
            submitBtn.disabled = true; submitBtn.textContent = 'Verifying...';
            authFetch(`${AUTH_API}/auth/claims/${claimId}/verify-phone-code`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code: code }), credentials: 'include'
            })
            .then(r => r.json())
            .then(d2 => {
              msg.classList.remove('hidden');
              if (d2.ok) {
                msg.className = 'text-sm mb-3 text-green-700'; msg.textContent = d2.message || 'Verified!';
                submitBtn.textContent = 'Verified';
                setTimeout(() => location.reload(), 1000);
              } else {
                msg.className = 'text-sm mb-3 text-red-600'; msg.textContent = d2.error || 'Invalid code.';
                submitBtn.disabled = false; submitBtn.textContent = 'Verify code';
              }
            })
            .catch(() => { msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-red-600'; msg.textContent = 'Network error.'; submitBtn.disabled = false; submitBtn.textContent = 'Verify code'; });
          };
        })
        .catch(() => { msg.classList.remove('hidden'); msg.className = 'text-sm mb-3 text-red-600'; msg.textContent = 'Network error.'; submitBtn.disabled = false; submitBtn.textContent = 'Send verification code'; });
      }
    };
  };
})();
